import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
// Changed from HashRouter to BrowserRouter
import { BrowserRouter as Router, Routes, Route, NavLink, Link, useParams, useNavigate, useLocation } from 'react-router-dom';

// Define these constants FIRST to ensure they are available when config is being defined
const APP_WHATSAPP_NUMBER = "447411429908";
const APP_EMAIL_ADDRESS = "wolvenworker@gmail.com";
const APP_PHONE_NUMBER_DISPLAY = "+447411429908";
const APP_CURRENCY_SYMBOL = "£"; // Moved currency symbol to a top-level constant

// --- config.js ---
// Centralized configuration for the website
const config = {
    shopName: "DTF Shirts Hub",
    shopTagline: "Your Design, Our Shirt, Your Style.", // New: Shop Tagline
    currencySymbol: APP_CURRENCY_SYMBOL, // Use constant
    whatsappNumber: APP_WHATSAPP_NUMBER, // Use constant
    whatsappPreMessage: "I would like to inquire about your service related to custom Dtf printed shirt.",
    whatsappOrderHeader: "*New Order from DTF Print Hub Website!*", // New: Configurable WhatsApp message header
    whatsappOrderFooter: "_Looking forward to your confirmation!_", // New: Configurable WhatsApp message footer

    // New: Social Media Links
    socialMediaLinks: [
        { name: 'Instagram', url: "", icon: 'Instagram' },
        { name: 'TikTok', url: "", icon: 'Tiktok' },
        // Add more social media links here if needed
        // { name: 'Facebook', url: "https://www.facebook.com/yourdtfprinthub", icon: 'Facebook' },
    ],
    emailAddress: APP_EMAIL_ADDRESS, // Use constant
    phoneNumber: APP_PHONE_NUMBER_DISPLAY, // Display format for phone number, use constant

    // New: Homepage Sections Configuration
    homepage: {
        showFeaturedProducts: true,
        featuredProductLimit: 3, // How many products to show in featured section
        showCustomerReviews: true,
        showAboutBlurb: true,
        showContactBlurb: true,

        // New: Hero Section Customization
        heroSection: {
            title: "Your Design  Our Shirt  Your Style",
            subtitle: "Custom DTF T-shirts that bring your ideas to life with vibrant prints and premium quality.",
            ctaText: "Shop Now",
            ctaLink: "/products",
            // Image links for hero section - add your specific image URLs here
            desktopImageUrl: 'https://placehold.co/1920x800/6A0DAD/6A0DAD', // Recommended: 1920x800 px
            mobileImageUrl: 'https://placehold.co/500x800/6A0DAD/6A0DAD' // Recommended: 600x800 px (or similar aspect ratio for mobile)
        },
        aboutBlurb: {
            title: "Our Story",
            text: "Welcome to DTF Print Hub, where creativity meets comfort! Founded in 2025, our journey began with a simple passion: to empower individuals to express themselves through unique, high-quality custom apparel. We specialize in Direct-to-Film (DTF) printing, ensuring vibrant colors and exceptional detail.",
            linkText: "Read More About Us",
            linkPath: "/about"
        },
        contactBlurb: {
            title: "Get in Touch",
            text: "Have a custom design idea or a question about our products? Reach out to us!",
            buttonText: "Contact Us",
            buttonLink: `https://wa.me/${APP_WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello, I have a question about DTF Print Hub products.")}` // Direct WhatsApp link, using constant
        }
    },

    // New: Animation Control
    enableAnimations: true,

    // New: Basic Theme Colors (for dynamic styling)
    theme: {
        primaryColor: "#7c3aed", // Equivalent to purple-600
        secondaryColor: "#4f46e5", // Equivalent to indigo-600
        accentColor: "#22c55e", // Equivalent to green-500
        textColor: "#1f2937", // Equivalent to gray-800
        lightBgColor: "#f3f4f6", // Equivalent to gray-100
    },

    // New: Header Font Size Configuration
    headerShopNameFontSize: "text-3xl", // Tailwind class like "text-3xl", "text-4xl", etc.
    headerTaglineFontSize: "text-sm", // Tailwind class like "text-sm", "text-base", etc.

    products: [
        {
            id: 'dtf-tshirt-1',
            name: 'Classic DTF Tee',
            description: 'High-quality cotton tee with vibrant DTF print. Perfect for everyday wear or special occasions.',
            longDescription: 'Our Classic DTF Tee is made from premium, soft-touch cotton, ensuring maximum comfort and durability. The Direct-to-Film (DTF) printing technology guarantees vibrant colors and exceptional detail that won\'t fade or crack. Customize it with your unique design and make a statement!',
            price: 19.99,
            imageUrl: 'https://placehold.co/600x600/F0F0F0/000000?text=Classic+Tee', // Base/fallback image. Recommended: 600x600 px or 1:1 aspect ratio.
            galleryImages: [ // Gallery images for image viewer. Recommended: 600x600 px or 1:1 aspect ratio.
                'https://placehold.co/600x600/FFFFFF/000000?text=White+Tee+Front',
                'https://placehold.co/600x600/FFFFFF/000000?text=White+Tee+Back',
                'https://placehold.co/600x600/FFFFFF/000000?text=White+Tee+Detail',
            ],
            colorOptions: [
                {
                    name: 'White',
                    hex: '#FFFFFF',
                    imageUrl: 'https://placehold.co/600x600/FFFFFF/000000?text=White+Tee+Front', // Recommended: 600x600 px or 1:1 aspect ratio.
                    printSizeImages: {
                        'A3': 'https://placehold.co/600x600/FFFFFF/000000?text=White+Tee+A3+Print', // Recommended: 600x600 px or 1:1 aspect ratio.
                        'A4': 'https://placehold.co/600x600/FFFFFF/000000?text=White+Tee+A4+Print' // Recommended: 600x600 px or 1:1 aspect ratio.
                    }
                },
                {
                    name: 'Black',
                    hex: '#000000',
                    imageUrl: 'https://placehold.co/600x600/000000/FFFFFF?text=Black+Tee+Front',
                    printSizeImages: {
                        'A3': 'https://placehold.co/600x600/000000/FFFFFF?text=Black+Tee+A3+Print',
                        'A4': 'https://placehold.co/600x600/000000/FFFFFF?text=Black+Tee+A4+Print'
                    }
                },
                {
                    name: 'Red',
                    hex: '#FF0000',
                    imageUrl: 'https://placehold.co/600x600/FF0000/FFFFFF?text=Red+Tee+Front',
                    printSizeImages: {
                        'A3': 'https://placehold.co/600x600/FF0000/FFFFFF?text=Red+Tee+A3+Print',
                        'A4': 'https://placehold.co/600x600/FF0000/FFFFFF?text=Red+Tee+A4+Print'
                    }
                },
                {
                    name: 'Blue',
                    hex: '#0000FF',
                    imageUrl: 'https://placehold.co/600x600/0000FF/FFFFFF?text=Blue+Tee+Front',
                    printSizeImages: {
                        'A3': 'https://placehold.co/600x600/0000FF/FFFFFF?text=Blue+Tee+A3+Print',
                        'A4': 'https://placehold.co/600x600/0000FF/FFFFFF?text=Blue+Tee+A4+Print'
                    }
                }
            ],
            sizeOptions: ['S', 'M', 'L', 'XL', '2XL'],
            materialOptions: ['100% Cotton', 'Poly-Blend'],
            printSizeOptions: ['A3', 'A4'], // New: Print Size Options
            specifications: [
                { title: 'Fabric Weight', detail: '180 GSM' },
                { title: 'Print Method', detail: 'Direct-to-Film (DTF)' },
                { title: 'Care Instructions', detail: 'Machine wash cold, inside out. Tumble dry low. Do not iron directly on print.' }
            ],
            faqs: [
                { question: 'How long does shipping take?', answer: 'Typically 5-7 business days within Pakistan.' },
                { question: 'Can I provide my own design?', answer: 'Yes, please contact us via WhatsApp for custom design inquiries.' }
            ]
        },
        {
            id: 'dtf-tshirt-2',
            name: 'Premium Crewneck',
            description: 'Soft and comfortable crewneck with a large DTF print area.',
            longDescription: 'Experience ultimate comfort with our Premium Crewneck. Crafted from a heavier cotton blend, it\'s perfect for cooler weather. The spacious front allows for larger, more intricate DTF designs, making your custom artwork truly stand out. Durable and stylish for any occasion.',
            price: 24.99,
            imageUrl: 'https://placehold.co/600x600/E0E0E0/000000?text=Crewneck', // Recommended: 600x600 px or 1:1 aspect ratio.
            galleryImages: [ // Recommended: 600x600 px or 1:1 aspect ratio.
                'https://placehold.co/600x600/808080/FFFFFF?text=Grey+Crewneck+Front',
                'https://placehold.co/600x600/808080/FFFFFF?text=Grey+Crewneck+Detail',
            ],
            colorOptions: [
                { name: 'Grey', hex: '#808080', imageUrl: 'https://placehold.co/600x600/808080/FFFFFF?text=Grey+Crewneck+Front' },
                { name: 'Navy', hex: '#000080', imageUrl: 'https://placehold.co/600x600/000080/FFFFFF?text=Navy+Crewneck+Front' }
            ],
            sizeOptions: ['M', 'L', 'XL'],
            materialOptions: ['Cotton-Poly Blend'],
            printSizeOptions: ['A3', 'A4', 'Letter'], // New: Print Size Options
            specifications: [
                { title: 'Fabric Weight', detail: '220 GSM' },
                { title: 'Print Method', detail: 'Direct-to-Film (DTF)' },
                { title: 'Care Instructions', detail: 'Machine wash cold. Hang dry for best results.' }
            ],
            faqs: [
                { question: 'Is this suitable for winter?', answer: 'Yes, it\'s a heavier fabric suitable for mild to moderate cold.' }
            ]
        },
        {
            id: 'dtf-tshirt-3',
            name: 'Sporty V-Neck',
            description: 'Lightweight V-neck, ideal for active wear with custom DTF graphics.',
            longDescription: 'Stay cool and stylish with our Sporty V-Neck. Its lightweight, breathable fabric makes it perfect for workouts or casual outings. The DTF print holds up beautifully, offering a personalized touch to your active wardrobe. Designed for comfort and performance.',
            price: 21.99,
            imageUrl: 'https://placehold.co/600x600/D0D0D0/000000?text=V-Neck', // Recommended: 600x600 px or 1:1 aspect ratio.
            galleryImages: [ // Recommended: 600x600 px or 1:1 aspect ratio.
                'https://placehold.co/600x600/FFFFFF/000000?text=White+V-Neck+Front',
                'https://placehold.co/600x600/D0D0D0/000000?text=V-Neck+Side',
            ],
            colorOptions: [
                { name: 'White', hex: '#FFFFFF', imageUrl: 'https://placehold.co/600x600/FFFFFF/000000?text=White+V-Neck+Front' },
                { name: 'Black', hex: '#000000', imageUrl: 'https://placehold.co/600x600/000000/FFFFFF?text=Black+V-Neck+Front' }
            ],
            sizeOptions: ['S', 'M', 'L'],
            materialOptions: ['Performance Polyester'],
            printSizeOptions: ['A4'], // New: Print Size Options
            specifications: [
                { title: 'Fabric Weight', detail: '160 GSM' },
                { title: 'Print Method', detail: 'Direct-to-Film (DTF)' },
                { title: 'Care Instructions', detail: 'Machine wash cold. Do not bleach.' }
            ],
            faqs: [
                { question: 'Is the fabric moisture-wicking?', answer: 'Yes, it\'s designed with moisture-wicking properties.' }
            ]
        },
        {
            id: 'dtf-tshirt-4',
            name: 'Hoodie with DTF Print',
            description: 'Cozy hoodie, perfect for custom designs and warmth.',
            longDescription: 'Our DTF Printed Hoodie offers supreme comfort and warmth, making it an essential for any wardrobe. The large print area on the front and back allows for bold and expressive custom designs. Made with a soft fleece lining, it\'s perfect for chilly evenings or a casual, stylish look.',
            price: 39.99,
            imageUrl: 'https://placehold.co/600x600/C0C0C0/000000?text=Hoodie', // Recommended: 600x600 px or 1:1 aspect ratio.
            galleryImages: [ // Recommended: 600x600 px or 1:1 aspect ratio.
                'https://placehold.co/600x600/A0A0A0/FFFFFF?text=Grey+Hoodie+Front',
                'https://placehold.co/600x600/A0A0A0/FFFFFF?text=Grey+Hoodie+Back',
                'https://placehold.co/600x600/36454F/FFFFFF?text=Charcoal+Hoodie+Front',
            ],
            colorOptions: [
                { name: 'Heather Grey', hex: '#A0A0A0', imageUrl: 'https://placehold.co/600x600/A0A0A0/FFFFFF?text=Grey+Hoodie+Front' },
                { name: 'Charcoal', hex: '#36454F', imageUrl: 'https://placehold.co/600x600/36454F/FFFFFF?text=Charcoal+Hoodie+Front' }
            ],
            sizeOptions: ['M', 'L', 'XL', '2XL'],
            materialOptions: ['Cotton-Poly Fleece'],
            printSizeOptions: ['A3'], // New: Print Size Options
            specifications: [
                { title: 'Fabric Weight', detail: '280 GSM' },
                { title: 'Print Method', detail: 'Direct-to-Film (DTF)' },
                { title: 'Features', detail: 'Drawstring hood, ribbed cuffs and hem, kangaroo pocket.' }
            ],
            faqs: [
                { question: 'Is the print durable after washing?', answer: 'Yes, DTF prints are highly durable and resist cracking or fading when cared for properly.' }
            ]
        }
    ],
    reviews: [
        {
            id: 'rev-1',
            customerName: 'Aisha K.',
            rating: 5,
            text: 'Absolutely love my custom tee! The print quality is amazing and the shirt is super soft. Will definitely order again!',
            productId: 'Classic DTF Tee'
        },
        {
            id: 'rev-2',
            customerName: 'Bilal R.',
            rating: 4,
            text: 'Great product and fast delivery. The colors are vibrant, just as I wanted. Highly recommend DTF Print Hub.',
            productId: 'Premium Crewneck'
        },
        {
            id: 'rev-3',
            customerName: 'Sara M.',
            rating: 5,
            text: 'Fantastic service! They helped me with my design and the final product exceeded my expectations. Five stars!',
            productId: 'Sporty V-Neck'
        },
        {
            id: 'rev-4',
            customerName: 'Usman H.',
            rating: 4,
            text: 'The t-shirt material is very comfortable. Print looks good, though it took a little longer to arrive than expected.',
            productId: 'Classic DTF Tee'
        },
        {
            id: 'rev-5',
            customerName: 'Fatima N.',
            rating: 5,
            text: 'Perfect custom gift! My friend loved it. The print is sharp and the fabric feels premium. Thank Thank you!',
            productId: 'Hoodie with DTF Print'
        },
        {
            id: 'rev-6',
            customerName: 'Zain A.',
            rating: 5,
            text: 'Incredible detail on the print. I\'m amazed at how well my complex design came out. Super happy with my purchase.',
            productId: 'Premium Crewneck'
        },
        {
            id: 'rev-7',
            customerName: 'Hafsa B.',
            rating: 4,
            text: 'Good quality shirt and print. The process was smooth. Would be even better with more color options for shirts.',
            productId: 'Sporty V-Neck'
        },
        {
            id: 'rev-8',
            customerName: 'Imran S.',
            rating: 5,
            text: 'My go-to for custom tees now. The DTF print is far superior to other methods I\'ve tried. Durable and vibrant.',
            productId: 'Classic DTF Tee'
        },
    ],
    policy: {
        privacyPolicy: {
            title: "Privacy Policy",
            content: `
Welcome to DTF Print Hub's Privacy Policy. We are committed to protecting your privacy and ensuring you have a positive experience on our website. This policy outlines how we collect, use, and protect your personal information.

**1. Information We Collect**

* **Personal Information:** When you place an order, create an account, or contact us, we may collect personal information such as your name, email address, shipping address, phone number, and payment details.
* **Order Details:** We collect information about your purchases, including product details, customization choices (e.g., color, size, material, print size), and order history.
* **Communication Data:** If you contact us via WhatsApp, email, or other means, we collect the content of your communications.
* **Usage Data:** We may collect non-personal information about how you interact with our website, such as IP address, browser type, pages visited, and time spent on pages. This helps us improve our website and services.

**2. How We Use Your Information**

We use your information for the following purposes:

* **Order Fulfillment:** To process your orders, including printing, packaging, shipping, and delivery of custom T-shirts.
* **Customer Service:** To respond to your inquiries, provide support, and resolve any issues you may have.
* **Communication:** To send you order confirmations, shipping updates, and important service-related announcements.
* **Website Improvement:** To analyze website usage, identify trends, and improve our website's functionality, user experience, and product offerings.
* **Marketing (Optional):** With your consent, we may send you promotional emails about new products, special offers, or other updates. You can opt out at any time.

**3. Data Sharing and Disclosure**

We do not sell, rent, or trade your personal information to third parties. We may share your information with:

* **Service Providers:** Trusted third-party partners who assist us in operating our business, such as payment processors, shipping carriers (e.g., courier services), and IT service providers. These partners are obligated to protect your information and use it only for the purposes for which we disclose it to them.
* **Legal Compliance:** If required by law, we may disclose your information to comply with legal obligations, protect our rights, or respond to legal processes.

**4. Data Security**

We implement reasonable security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. This includes using secure servers, encryption, and regular security audits. However, no method of transmission over the internet or electronic storage is 100% secure.

**5. Your Choices and Rights**

* **Access and Correction:** You have the right to access and correct your personal information held by us. Please contact us to make such requests.
* **Opt-Out:** You can opt out of receiving marketing communications from us at any time by following the unsubscribe instructions in the emails or by contacting us directly.
* **Data Deletion:** You can request the deletion of your personal information, subject to certain legal and operational requirements.

**6. Changes to This Policy**

We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any significant changes by posting the updated policy on your website.

**7. Contact Us**

If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
Email: ${APP_EMAIL_ADDRESS}
WhatsApp: ${APP_WHATSAPP_NUMBER}
`
        },
        refundPolicy: {
            title: "Refund Policy",
            content: `
At DTF Print Hub, we strive for your complete satisfaction with every custom T-shirt order. Due to the personalized nature of our products, our refund policy is as follows:

**1. Eligibility for Refund/Replacement**

You may be eligible for a refund or replacement if:

* **Defective Product:** The product arrives damaged, torn, or with a manufacturing defect.
* **Incorrect Order:** You received a product that is significantly different from what you ordered (e.g., wrong size, color, design, or print size) due to our error.
* **Print Quality Issues:** The DTF print is significantly faded, cracked upon arrival, or has obvious print errors that are clearly our fault.

**2. Non-Eligibility for Refund/Replacement**

We generally do not offer refunds or replacements for:

* **Customer Error:** Orders placed with incorrect design files, spelling errors, wrong size/color/material/print size selections made by the customer.
* **Subjective Dislikes:** Dissatisfaction with the product that is not due to a defect (e.g., "I don't like the shade of purple," "The design doesn't look as good as I imagined on the shirt"). We encourage reviewing proofs and product descriptions carefully.
* **Wear and Tear:** Damage caused by improper care, washing, or normal wear and tear after delivery. Please follow our care instructions.
* **Minor Variations:** Slight color variations between the screen display and the actual print due to monitor calibration differences.
* **Shipping Delays:** Delays caused by the shipping carrier, customs, or unforeseen circumstances outside our control.

**3. How to Request a Refund/Replacement**

If your order qualifies for a refund or replacement based on the eligibility criteria above:

* **Contact Us:** You must notify us within **[X, e.g., 7] days** of receiving your order.
* **Provide Details:** Include your order number, a clear description of the issue, and **attach clear photos or videos** demonstrating the defect or error.
* **Assessment:** Our team will review your request and the provided evidence. We may ask for additional information.
* **Resolution:** If your request is approved, we will offer a replacement product at no additional cost or issue a refund to your original payment method. Refunds typically take [Y, e.g., 5-10] business days to process.

**4. Return Process (if applicable)**

In some cases, we may require you to return the defective or incorrect item. If a return is necessary, we will provide you with clear instructions and cover the return shipping costs.

**5. Custom Design Approval**

For custom design orders, we will send you a digital proof for approval before printing. It is your responsibility to thoroughly review and approve this proof. Once approved, we are not responsible for errors (e.g., spelling, design placement) that were present in the approved proof.
`
        },
        shippingPolicy: {
            title: "Shipping Policy",
            content: `
Thank you for shopping with DTF Print Hub! Here’s what you need to know about our shipping process:

**1. Processing Time**

* **Standard Orders:** All orders are processed within **[X, e.g., 2-3] business days** (Monday to Friday, excluding public holidays) after design approval (if applicable) and payment confirmation.
* **Large/Complex Orders:** For bulk or intricate custom orders, processing times may be longer. We will communicate the estimated timeline upfront.

**2. Shipping Rates & Delivery Estimates**

Shipping costs are calculated at checkout based on your location and the weight/dimensions of your order.

* **Domestic Shipping (within Pakistan):**
    * **Estimated Delivery:** Typically **[Y, e.g., 3-7] business days** after dispatch.
    * **Carrier:** We partner with reputable local courier services to ensure reliable delivery.
* **International Shipping:**
    * Currently, we primarily serve customers within Pakistan. For international inquiries, please contact us directly via WhatsApp or email to discuss possibilities and obtain a custom shipping quote.

**3. Order Tracking**

Once your order has been dispatched, you will receive a shipping confirmation email (if email is provided during checkout) or a WhatsApp message with your tracking number and a link to track your package. Please allow 24-48 hours for the tracking information to become active.

**4. Shipping Address Accuracy**

Please ensure your shipping address is complete and accurate at the time of placing your order. We are not responsible for orders shipped to incorrectly provided addresses. If an order is returned due to an valid address, additional shipping fees may apply for re-delivery.

**5. Undeliverable Packages**

If a package is returned to us as undeliverable due to reasons such as "addressee unknown," "no such address," or "refused," we will contact you to arrange re-delivery. Additional shipping charges will apply.

**6. Damaged or Lost Packages**

* **Damaged:** If your package arrives damaged, please take photos of the damaged packaging and product immediately and contact us within 24 hours of delivery.
* **Lost:** If your tracking information shows that your package was delivered but you have not received it, please contact the shipping carrier first. If the issue persists, contact us for assistance.
`
        },
        termsOfService: {
            title: "Terms of Service",
            content: `
Welcome to DTF Print Hub! These Terms of Service ("Terms") govern your use of our website and services. By accessing or using our website, you agree to comply with and be bound by these Terms.

**1. Acceptance of Terms**

By accessing and using the DTF Print Hub website and purchasing products from us, you agree to be bound by these Terms and all applicable laws and regulations. If you do not agree with any of these Terms, you are prohibited from using or accessing this site.

**2. Services Offered**

DTF Print Hub provides custom Direct-to-Film (DTF) T-shirt printing services. This includes offering a selection of apparel items (T-shirts, hoodies, etc.) and the ability to apply custom designs provided by the customer or selected from our available options.

**3. Orders and Payments**

* **Order Placement:** Orders are placed through our website's cart system and finalized via WhatsApp communication for design approval and payment.
* **Payment:** All payments must be completed and confirmed before production of custom items begins. We accept various payment methods as communicated during the WhatsApp order finalization.
* **Pricing:** Prices are listed in ${APP_CURRENCY_SYMBOL} (PKR) and are subject to change without prior notice.
`
        }
    }
};

// --- Lucide React Icons (simulated for demonstration) ---
// In a real project, you would install and import these:
// import { ShoppingCart, Menu, X, Instagram, Tiktok, Mail, Phone, Home, Shirt, Star, Info, FileText, ChevronRight, ChevronDown, Plus, Minus, CheckCircle, Package, User, Search, ZoomIn, ZoomOut } from 'lucide-react';

// For this self-contained example, we'll use inline SVGs or simple text for icons.
// To use actual lucide-react icons, you would need to install them: `npm install lucide-react`
// and then import them like: `import { ShoppingCart, Menu } from 'lucide-react';`

// Placeholder for Lucide icons
const Icon = ({ name, className }) => {
    const iconMap = {
        ShoppingCart: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
        ),
        Menu: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="18" x2="20" y2="18" />
            </svg>
        ),
        X: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),
        Instagram: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
        ),
        Tiktok: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className={className}>
                {/* Updated TikTok icon path */}
                <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
        ),
        Mail: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
        ),
        Phone: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
        ),
        Home: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
            </svg>
        ),
        Shirt: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="M20.38 3.46 16 2a4 4 0 0 1-4 4V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4a4 4 0 0 1 4 4l4.38-1.46a2 2 0 0 1 2.24.93L22 7l-1.47 1.47a2 2 0 0 1-2.24-.93z" />
            </svg>
        ),
        Star: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
        ),
        Info: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
            </svg>
        ),
        FileText: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />
            </svg>
        ),
        ChevronRight: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="m9 18 6-6-6-6" />
            </svg>
        ),
        ChevronLeft: ( // Added for image gallery
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="m15 18-6-6 6-6" />
            </svg>
        ),
        ChevronDown: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="m6 9 6 6 6-6" />
            </svg>
        ),
        Plus: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
        ),
        Minus: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
        ),
        CheckCircle: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-8.5" /><path d="m22 4-7 7-4-4" />
            </svg>
        ),
        Package: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="m7.5 4.27 9 5.15" /><path d="M2.52 6.13 12 11.5l9.48-5.37" /><path d="m7.5 19.73 9-5.15" /><path d="M12 22.5l-9.48-5.37L2.52 6.13" /><path d="M22 6.13 12.02 11.5 12 22.5" />
            </svg>
        ),
        User: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
        ),
        Whatsapp: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 58" className={className}>
                <g>
                    <path fill="#2CB742" d="M0,58l4.988-14.963C2.457,38.78,1,33.812,1,28.5C1,12.76,13.76,0,29.5,0S58,12.76,58,28.5
                        S45.24,57,29.5,57c-4.789,0-9.299-1.187-13.26-3.273L0,58z"/>
                    <path fill="#FFFFFF" d="M47.683,37.985c-1.316-2.487-6.169-5.331-6.169-5.331c-1.098-0.626-2.423-0.696-3.049,0.42
                        c0,0-1.577,1.891-1.978,2.163c-1.832,1.241-3.529,1.193-5.242-0.52l-3.981-3.981l-3.981-3.981c-1.713-1.713-1.761-3.41-0.52-5.242
                        c0.272-0.401,2.163-1.978,2.163-1.978c1.116-0.627,1.046-1.951,0.42-3.049c0,0-2.844-4.853-5.331-6.169
                        c-1.058-0.56-2.357-0.364-3.203,0.482l-1.758,1.758c-5.577,5.577-2.831,11.873,2.746,17.45l5.097,5.097l5.097,5.097
                        c5.577,5.577,11.873,8.323,17.45,2.746l1.758-1.758C48.048,40.341,48.243,39.042,47.683,37.985z"/>
                </g>
            </svg>
        ),
        // Added for navigation
        ZoomIn: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>),
        ZoomOut: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>),
    };
    return iconMap[name] || null;
};

// --- components/AnimatedSection.js ---
// Conditionally applies animations based on config.enableAnimations
const AnimatedSection = ({ animation = "fade-in-up", delay = 0, children, className = "" }) => {
    if (!config.enableAnimations) {
        return <div className={className}>{children}</div>;
    }

    return (
        <div
            className={`${className} animate-${animation}`}
            style={{ animationDelay: `${delay / 1000}s` }}
        >
            {children}
        </div>
    );
};

// --- CartContext.js ---
const CartContext = createContext();

// Custom hook to use the cart context
export const useCart = () => useContext(CartContext);

// Cart Provider component
export const CartProvider = ({ children }) => {
    // State for cart items, initialized from local storage
    const [cartItems, setCartItems] = useState(() => {
        try {
            const localCart = localStorage.getItem('dtf_cart');
            return localCart ? JSON.parse(localCart) : [];
        } catch (error) {
            console.error("Failed to parse cart from local storage:", error);
            return [];
        }
    });

    // State for shipping information, initialized from local storage
    const [shippingInfo, setShippingInfo] = useState(() => {
        try {
            const localShipping = localStorage.getItem('dtf_shipping_info');
            return localShipping ? JSON.parse(localShipping) : {};
        } catch (error) {
            console.error("Failed to parse shipping info from local storage:", error);
            return {};
        }
    });

    // Effect to save cart items to local storage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem('dtf_cart', JSON.stringify(cartItems));
        } catch (error) {
            console.error("Failed to save cart to local storage:", error);
        }
    }, [cartItems]);

    // Effect to save shipping info to local storage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('dtf_shipping_info', JSON.stringify(shippingInfo));
        } catch (error) {
            console.error("Failed to save shipping info to local storage:", error);
        }
    }, [shippingInfo]);

    // Function to add an item to the cart
    const addToCart = (product, selectedColor, selectedSize, selectedMaterial, selectedPrintSize, quantity) => {
        const newItem = {
            id: `${product.id}-${selectedColor.name}-${selectedSize}-${selectedMaterial}-${selectedPrintSize}`, // Unique ID for cart item
            productId: product.id,
            name: product.name,
            price: product.price,
            imageUrl: selectedColor.imageUrl || product.imageUrl,
            color: selectedColor.name,
            size: selectedSize,
            material: selectedMaterial,
            printSize: selectedPrintSize, // New: printSize
            quantity: quantity,
        };

        setCartItems(prevItems => {
            // Check if item with same options already exists
            const existingItemIndex = prevItems.findIndex(item =>
                item.productId === newItem.productId &&
                item.color === newItem.color &&
                item.size === newItem.size &&
                item.material === newItem.material &&
                item.printSize === newItem.printSize // New: check printSize
            );

            if (existingItemIndex > -1) {
                // If exists, update quantity
                const updatedItems = [...prevItems];
                updatedItems[existingItemIndex].quantity += quantity;
                return updatedItems;
            } else {
                // Otherwise, add new item
                return [...prevItems, newItem];
            }
        });
    };

    // Function to remove an item from the cart
    const removeFromCart = (id) => {
        setCartItems(prevItems => prevItems.filter(item => item.id !== id));
    };

    // Function to update the quantity of an item in the cart
    const updateQuantity = (id, newQuantity) => {
        setCartItems(prevItems =>
            prevItems.map(item =>
                // FIX: Corrected syntax for ternary operator by adding missing closing brace
                item.id === id ? { ...item, quantity: Math.max(1, newQuantity) } : item
            )
        );
    };

    // Function to clear all items from the cart
    const clearCart = () => {
        setCartItems([]);
    };

    // Function to update shipping information
    const updateShippingInfo = (info) => {
        setShippingInfo(info);
    };

    // Function to clear shipping information
    const clearShippingInfo = () => {
        setShippingInfo({});
    };

    // Calculate total price of items in cart
    const getTotalPrice = () => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                shippingInfo,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                updateShippingInfo,
                clearShippingInfo,
                getTotalPrice,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

// --- components/Modal.js ---
const Modal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-auto transform transition-all duration-300 scale-100">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <div className="flex justify-end space-x-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-all duration-300 ease-in-out font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all duration-300 ease-in-out font-medium"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- components/ImageWithSkeleton.js ---
// New component for image loading animation
const ImageWithSkeleton = React.forwardRef(({ src, alt, className, objectFit = 'contain', ...props }, forwardedRef) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const internalImageRef = useRef(null);

    useEffect(() => {
        if (forwardedRef) {
            if (typeof forwardedRef === 'function') {
                forwardedRef(internalImageRef.current);
            } else {
                forwardedRef.current = internalImageRef.current;
            }
        }
    }, [forwardedRef]);

    useEffect(() => {
        const imgElement = internalImageRef.current;

        if (!imgElement) {
            setIsLoading(true);
            setIsError(false);
            return;
        }

        if (!src) {
            setIsLoading(false);
            setIsError(true);
            return;
        }

        // Reset states for a new image load attempt.
        setIsLoading(true);
        setIsError(false);

        const handleLoad = () => {
            setIsLoading(false);
            setIsError(false);
        };

        const handleError = () => {
            setIsLoading(false);
            setIsError(true);
        };

        imgElement.addEventListener('load', handleLoad);
        imgElement.addEventListener('error', handleError);

        // Check if the image is already complete (e.g., cached).
        if (imgElement.complete) {
            if (imgElement.naturalHeight !== 0) {
                setIsLoading(false);
                setIsError(false);
            } else {
                setIsLoading(false);
                setIsError(true);
            }
        }

        return () => {
            imgElement.removeEventListener('load', handleLoad);
            imgElement.removeEventListener('error', handleError);
        };
    }, [src]);

    return (
        <div className={`relative ${className} flex items-center justify-center overflow-hidden`}>
            {/* Show skeleton/error overlay based on opacity */}
            {(isLoading || isError) && (
                <div className={`absolute inset-0 bg-gray-200 flex items-center justify-center transition-opacity duration-300
                            ${isLoading ? 'animate-pulse opacity-100' : 'opacity-0'}
                            ${isError && !isLoading ? 'bg-gray-300 opacity-100' : ''}`}
                >
                    <span className="text-gray-500 text-sm">{isLoading ? 'Loading...' : 'Image failed to load.'}</span>
                </div>
            )}
            <img
                ref={internalImageRef}
                src={src}
                alt={alt}
                // Control visibility with opacity, allowing transitions without display: none
                className={`w-full h-full object-cover transition-opacity duration-300`}
                style={{
                    objectFit: objectFit,
                    opacity: isLoading || isError ? 0 : 1,
                }}
                {...props}
            />
        </div>
    );
});


// --- components/Header.js ---
const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { cartItems } = useCart();
    const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

    const navLinks = [
        { name: 'Home', path: '/', icon: 'Home' },
        { name: 'Products', path: '/products', icon: 'Shirt' },
        { name: 'Reviews', path: '/reviews', icon: 'Star' },
        { name: 'About', path: '/about', icon: 'Info' },
        { name: 'Policy', path: '/policy', icon: 'FileText' },
    ];

    return (
        <header className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg sticky top-0 z-40 font-inter"
            style={{ backgroundImage: `linear-gradient(to right, ${config.theme.primaryColor}, ${config.theme.secondaryColor})` }}
        >
            <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                {/* Company Name / Home Button */}
                <Link
                    to="/"
                    onClick={() => window.scrollTo(0, 0)} // Explicitly scroll to top on logo click
                    className={`font-bold tracking-tight transform transition-transform duration-300 hover:scale-105 hover:text-purple-200 rounded-md p-2 ${config.headerShopNameFontSize}`}
                >
                    {config.shopName}
                    {config.shopTagline && (
                        <span className={`block font-normal opacity-80 ${config.headerTaglineFontSize}`}>{config.shopTagline}</span>
                    )}
                </Link>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center space-x-6">
                    {navLinks.map((link) => (
                        <NavLink
                            key={link.name}
                            to={link.path}
                            onClick={() => window.scrollTo(0, 0)} // Explicitly scroll to top on nav link click
                            className={({ isActive }) =>
                                `relative text-lg font-medium py-2 px-4 rounded-md transition-all duration-300 ease-in-out
                                 hover:bg-white hover:bg-opacity-20 hover:shadow-md
                                 ${isActive ? 'border-b-2 border-white bg-white bg-opacity-10 shadow-inner' : ''}
                                 group overflow-hidden`
                            }
                        >
                            {link.name}
                            {/* Animated Border Effect */}
                            <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                        </NavLink>
                    ))}
                    {/* Cart Icon for Desktop */}
                    <NavLink
                        to="/cart"
                        onClick={() => window.scrollTo(0, 0)} // Explicitly scroll to top on cart link click
                        className={({ isActive }) =>
                            `relative text-lg font-medium py-2 px-4 rounded-md transition-all duration-300 ease-in-out
                             hover:bg-white hover:bg-opacity-20 hover:shadow-md
                             ${isActive ? 'border-b-2 border-white bg-white bg-opacity-10 shadow-inner' : ''}
                             group flex items-center space-x-2 overflow-visible` /* Changed overflow-hidden to overflow-visible */
                        }
                    >
                        <Icon name="ShoppingCart" className="w-6 h-6 transform transition-transform duration-300 group-hover:scale-110" />
                        {cartItemCount > 0 && (
                            <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center z-30 animate-bounce"> {/* Adjusted top and right */}
                                {cartItemCount}
                            </span>
                        )}
                        <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                    </NavLink>
                </nav>

                {/* Mobile Hamburger Menu & Cart Icon */}
                <div className="md:hidden flex items-center space-x-4">
                    <NavLink
                        to="/cart"
                        onClick={() => window.scrollTo(0, 0)} // Explicitly scroll to top on cart link click
                        className={({ isActive }) =>
                            `relative text-lg font-medium p-2 rounded-md transition-all duration-300 ease-in-out
                             hover:bg-white hover:bg-opacity-20 hover:shadow-md
                             ${isActive ? 'border-b-2 border-white bg-white bg-opacity-10 shadow-inner' : ''}
                             group flex items-center justify-center overflow-visible` /* Changed overflow-hidden to overflow-visible */
                        }
                    >
                        <Icon name="ShoppingCart" className="w-6 h-6 transform transition-transform duration-300 group-hover:scale-110" />
                        {cartItemCount > 0 && (
                            <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center z-30 animate-bounce"> {/* Adjusted top and right */}
                                {cartItemCount}
                            </span>
                        )}
                        <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                    </NavLink>
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="p-2 rounded-md hover:bg-white hover:bg-opacity-20 transition-all duration-300 ease-in-out"
                    >
                        {isMobileMenuOpen ? (
                            <Icon name="X" className="w-6 h-6" />
                        ) : (
                            <Icon name="Menu" className="w-6 h-6" />
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            <nav
                className={`md:hidden bg-purple-700 bg-opacity-95 transition-all duration-300 ease-in-out overflow-hidden ${
                    isMobileMenuOpen ? 'max-h-screen py-4' : 'max-h-0'
                }`}
                style={{ backgroundColor: config.theme.primaryColor + 'E0' }} // Adding opacity to primary color
            >
                <ul className="flex flex-col items-center space-y-4">
                    {navLinks.map((link) => (
                        <li key={link.name}>
                            <NavLink
                                to={link.path}
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    window.scrollTo(0, 0); // Explicitly scroll to top on nav link click
                                }}
                                className={({ isActive }) =>
                                    `relative text-lg font-medium py-2 px-4 rounded-md transition-all duration-300 ease-in-out
                                     hover:bg-white hover:bg-opacity-20 hover:shadow-md
                                     ${isActive ? 'border-b-2 border-white bg-white bg-opacity-10 shadow-inner' : ''}
                                     group overflow-hidden block w-full text-center`
                                }
                            >
                                {link.name}
                                <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>
        </header>
    );
};

// --- components/Footer.js ---
const Footer = () => {
    const quickLinks = [
        { name: 'Products', path: '/products' },
        { name: 'Reviews', path: '/reviews' },
        { name: 'About', path: '/about' },
        { name: 'Policy', path: '/policy' },
    ];

    return (
        <footer className="bg-gray-900 text-gray-300 font-inter">
            {/* Sub-footer */}
            <div className="container mx-auto px-6 py-10 flex flex-col md:flex-row justify-around items-center md:items-start gap-8 border-b border-gray-700 text-center md:text-left">
                {/* Company Info */}
                <AnimatedSection animation="fade-in-up" delay={50}>
                    <div className="space-y-4">
                        <h3 className="text-xl font-bold text-white mb-2">{config.shopName}</h3>
                        {config.shopTagline && <p className="text-sm max-w-xs">{config.shopTagline}</p>}
                        <p className="text-sm max-w-xs">Your one-stop shop for custom-printed DTF T-shirts. Express yourself!</p>
                    </div>
                </AnimatedSection>

                {/* Quick Links */}
                <AnimatedSection animation="fade-in-up" delay={150}>
                    <div>
                        <h3 className="text-xl font-bold text-white mb-4">Quick Links</h3>
                        <ul className="space-y-2">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-white transition-colors duration-300 relative group"
                                    >
                                        {link.name}
                                        <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </AnimatedSection>

                {/* Connect With Us */}
                <AnimatedSection animation="fade-in-up" delay={250}>
                    <div>
                        <h3 className="text-xl font-bold text-white mb-4">Connect With Us</h3>
                        <div className="flex justify-center md:justify-start space-x-4 mb-4">
                            {config.socialMediaLinks.map((social, index) => (
                                <a
                                    key={index}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-pink-500 transition-all duration-300 transform hover:scale-125"
                                >
                                    <Icon name={social.icon} className="w-7 h-7" />
                                </a>
                            ))}
                        </div>
                        <div className="space-y-2">
                            <a
                                href={`mailto:${APP_EMAIL_ADDRESS}`}
                                className="flex items-center justify-center md:justify-start space-x-2 text-gray-400 hover:text-white transition-colors duration-300 group"
                            >
                                <Icon name="Mail" className="w-5 h-5" />
                                <span>{APP_EMAIL_ADDRESS}</span>
                                <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
                            </a>
                            <a
                                href={`tel:${APP_WHATSAPP_NUMBER}`}
                                className="flex items-center justify-center md:justify-start space-x-2 text-gray-400 hover:text-white transition-colors duration-300 group"
                            >
                                <Icon name="Phone" className="w-5 h-5" />
                                <span>{APP_PHONE_NUMBER_DISPLAY}</span>
                                <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
                            </a>
                        </div>
                    </div>
                </AnimatedSection>
            </div>

            {/* Main Footer */}
            <div className="container mx-auto px-6 py-4 text-center text-gray-500 text-sm">
                <p>&copy; 2025 {config.shopName}. All rights reserved.</p>
                <p>Made with ❤️ by Wolvenworks</p>
            </div>
        </footer>
    );
};

// --- components/ProductCard.js ---
const ProductCard = ({ product }) => {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-purple-300 transition-all duration-300 ease-in-out overflow-hidden group transform hover:scale-[1.01]">
            {/* Adjusted Link to remove fixed height and use padding for flexibility */}
            <Link
                to={`/products/${product.id}`}
                onClick={() => window.scrollTo(0, 0)} // Explicitly scroll to top on product card image click
                className="w-full relative overflow-hidden flex items-center justify-center min-h-[250px] p-2"
            >
                <ImageWithSkeleton
                    src={product.imageUrl}
                    alt={product.name}
                    className="max-h-full max-w-full" // These are good for containing the image
                    objectFit="contain" // Explicitly ensure object-fit is contain
                />
            </Link>
            <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-purple-600" style={{ color: config.theme.primaryColor }}>{APP_CURRENCY_SYMBOL}{product.price.toFixed(2)}</span> {/* Used APP_CURRENCY_SYMBOL */}
                    <Link
                        to={`/products/${product.id}`}
                        onClick={() => window.scrollTo(0, 0)} // Explicitly scroll to top on "View Details" button click
                        className="relative inline-flex items-center px-4 py-2 bg-purple-600 text-white font-medium rounded-md"
                        style={{ backgroundColor: config.theme.primaryColor }}
                    >
                        View Details
                        <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                    </Link>
                </div>
            </div>
        </div>
    );
};

// --- components/ReviewCard.js ---
const ReviewCard = ({ review }) => {
    return (
        <div className="bg-white rounded-lg shadow-md p-6 min-w-[300px] max-w-[350px] flex-shrink-0 mx-4 border border-gray-200
                       transition-all duration-300 ease-in-out hover:shadow-xl hover:border-purple-300
                       flex flex-col items-center text-center">
            <p className="text-gray-600 text-sm italic mb-4">"{review.text}"</p>
            <div className="flex justify-center text-yellow-500 mb-2">
                {Array(review.rating).fill(0).map((_, i) => <Icon key={i} name="Star" className="w-4 h-4 fill-current" />)}
                {Array(5 - review.rating).fill(0).map((_, i) => <Icon key={i} name="Star" className="w-4 h-4" />)}
            </div>
            <h4 className="font-semibold text-gray-800 mb-1">{review.customerName}</h4>
            <p className="text-xs text-gray-500">Product: {review.productId || 'N/A'}</p>
        </div>
    );
};

// --- components/ReviewsSlider.js ---
const ReviewsSlider = ({ reviews }) => {
    const sliderRef = useRef(null);
    const wrapperRef = useRef(null);
    const animationFrameId = useRef(null);
    const startX = useRef(0);
    const currentTranslateX = useRef(0);
    const isDragging = useRef(false);
    const animationSpeed = 0.5; // pixels per frame

    // Conditionally duplicate reviews based on animation setting
    const reviewsToDisplay = useMemo(() => {
        return config.enableAnimations ? [...reviews, ...reviews, ...reviews] : reviews;
    }, [reviews]); // Removed config.enableAnimations from memo dependencies

    const animateSlider = useCallback(() => {
        if (!isDragging.current && wrapperRef.current && config.enableAnimations) { // Only animate if enabled
            currentTranslateX.current -= animationSpeed;
            // Recalculate wrapperWidth dynamically based on content rendered, especially crucial if not duplicated
            const singleReviewsWidth = wrapperRef.current.scrollWidth / (reviewsToDisplay.length / reviews.length);

            if (Math.abs(currentTranslateX.current) >= singleReviewsWidth) {
                currentTranslateX.current = 0;
            }
            wrapperRef.current.style.transform = `translateX(${currentTranslateX.current}px)`;
        }
        animationFrameId.current = requestAnimationFrame(animateSlider);
    }, [animationSpeed, reviewsToDisplay, reviews.length]);

    useEffect(() => {
        if (config.enableAnimations) { // Only start animation if enabled
            animationFrameId.current = requestAnimationFrame(animateSlider);
        }

        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [animateSlider]);

    const handleMouseDown = (e) => {
        isDragging.current = true;
        startX.current = e.clientX;
        if (animationFrameId.current) {
            cancelAnimationFrame(animationFrameId.current); // Pause animation
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        const dx = e.clientX - startX.current;
        currentTranslateX.current += dx;
        startX.current = e.clientX;
        if (wrapperRef.current) {
            wrapperRef.current.style.transform = `translateX(${currentTranslateX.current}px)`;
        }
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        if (config.enableAnimations) { // Resume animation only if enabled
            animationFrameId.current = requestAnimationFrame(animateSlider);
        }
    };

    const handleMouseLeave = () => {
        if (isDragging.current) {
            isDragging.current = false;
            if (config.enableAnimations) { // Resume animation only if enabled
                animationFrameId.current = requestAnimationFrame(animateSlider);
            }
        }
    };

    const handleTouchStart = (e) => {
        isDragging.current = true;
        startX.current = e.touches[0].clientX;
        if (animationFrameId.current) {
            cancelAnimationFrame(animationFrameId.current); // Pause animation
        }
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current) return;
        const dx = e.touches[0].clientX - startX.current;
        currentTranslateX.current += dx;
        startX.current = e.touches[0].clientX;
        if (wrapperRef.current) {
            wrapperRef.current.style.transform = `translateX(${currentTranslateX.current}px)`;
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
        if (config.enableAnimations) { // Resume animation only if enabled
            animationFrameId.current = requestAnimationFrame(animateSlider);
        }
    };


    return (
        <div
            ref={sliderRef}
            className="overflow-hidden py-8 relative"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div
                ref={wrapperRef}
                className="flex will-change-transform"
                style={{ transform: `translateX(${currentTranslateX.current}px)` }}
            >
                {reviewsToDisplay.map((review, index) => (
                    // Ensure unique key for duplicated reviews
                    <ReviewCard key={`${review.id}-${index}-${review.customerName}`} review={review} />
                ))}
            </div>
        </div>
    );
};

// --- pages/HomePage.js ---
const HomePage = () => {
    // State to determine if it's a mobile view
    const [isMobileView, setIsMobileView] = useState(false);

    useEffect(() => {
        // Function to check screen width and update state
        const checkScreenWidth = () => {
            // Tailwind's 'sm' breakpoint is 640px. Check for anything less than that.
            setIsMobileView(window.innerWidth < 640);
        };

        // Initial check
        checkScreenWidth();

        // Add event listener for window resize
        window.addEventListener('resize', checkScreenWidth);

        // Cleanup: remove event listener when component unmounts
        return () => window.removeEventListener('resize', checkScreenWidth);
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    // Determine which image URL to use based on isMobileView state
    const heroImageUrl = isMobileView
        ? config.homepage.heroSection.mobileImageUrl
        : config.homepage.heroSection.desktopImageUrl;

    return (
        <div className="font-inter">
            {/* Hero Section */}
            <section
                className="relative h-[600px] bg-center flex items-center justify-center text-white text-center rounded-b-xl shadow-lg"
                style={{
                    backgroundImage: `url('${heroImageUrl}')`,
                    backgroundSize: 'cover', // Always cover to prevent background color showing
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundColor: 'transparent' // Ensure no solid background color behind image
                }}
            >
                {/* <div className="absolute inset-0 bg-black opacity-50 rounded-b-xl"></div> Removed in prior step */}
                <div className="relative z-10 p-6 max-w-3xl mx-auto">
                    <AnimatedSection animation="fade-in-up" delay={0}>
                        <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-shadow-lg">
                            {config.homepage.heroSection.title}
                        </h1>
                    </AnimatedSection>
                    <AnimatedSection animation="fade-in-up" delay={200}>
                        <p className="text-xl md:text-2xl mb-8 text-shadow-md">
                            {config.homepage.heroSection.subtitle}
                        </p>
                    </AnimatedSection>
                    <AnimatedSection animation="pop-in" delay={400}>
                        <Link
                            to={config.homepage.heroSection.ctaLink}
                            className="relative inline-flex items-center px-8 py-4 bg-purple-600 text-white font-bold text-lg rounded-full shadow-lg
                                   hover:bg-purple-700 hover:shadow-xl hover:scale-105 transform-gpu
                                   transition-all duration-300 ease-in-out group overflow-hidden"
                            style={{ backgroundColor: config.theme.primaryColor, '--tw-ring-color': config.theme.primaryColor }}
                        >
                            {config.homepage.heroSection.ctaText}
                            <span className="absolute inset-0 border-2 border-transparent rounded-full transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>

            {/* Featured Products Section */}
            {config.homepage.showFeaturedProducts && (
                <section className="container mx-auto px-6 py-16">
                    <AnimatedSection animation="fade-in-up">
                        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8">
                            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">Featured Products</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                                {config.products.slice(0, config.homepage.featuredProductLimit).map((product, index) => (
                                    <AnimatedSection key={product.id} animation="fade-in-up" delay={index * 100 + 100}>
                                        <ProductCard product={product} />
                                    </AnimatedSection>
                                ))}
                            </div>
                            <div className="text-center mt-12">
                                <Link
                                    to="/products"
                                    className="relative inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-md shadow-md
                                   hover:bg-indigo-700 hover:shadow-lg hover:scale-105 transform-gpu
                                   transition-all duration-300 ease-in-out group overflow-hidden"
                                    style={{ backgroundColor: config.theme.secondaryColor }}
                                >
                                    View All Products
                                    <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                                </Link>
                            </div>
                        </div>
                    </AnimatedSection>
                </section>
            )}

            {/* What Our Customers Says Section */}
            {config.homepage.showCustomerReviews && (
                <section className="bg-gray-100 py-16" style={{ backgroundColor: config.theme.lightBgColor }}>
                    <AnimatedSection animation="fade-in-up">
                        <div className="container mx-auto bg-white rounded-lg shadow-md border border-gray-200 p-8">
                            <h2 className="text-4xl font-bold text-center text-gray-800 mb-8">What Our Customers Say</h2>
                            <ReviewsSlider reviews={config.reviews} />
                            <div className="text-center mt-8">
                                <Link
                                    to="/reviews"
                                    className="relative inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-md shadow-md
                                   hover:bg-purple-700 hover:shadow-lg hover:scale-105 transform-gpu
                                   transition-all duration-300 ease-in-out group overflow-hidden"
                                    style={{ backgroundColor: config.theme.primaryColor }}
                                >
                                    More Reviews
                                    <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                                </Link>
                            </div>
                        </div>
                    </AnimatedSection>
                </section>
            )}

            {/* Homepage About Blurb */}
            {config.homepage.showAboutBlurb && config.homepage.aboutBlurb && (
                <section className="container mx-auto px-6 py-16">
                    <AnimatedSection animation="fade-in-up">
                        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 text-center">
                            <h2 className="text-3xl font-bold text-gray-800 mb-4">{config.homepage.aboutBlurb.title}</h2>
                            <p className="text-gray-700 mb-6">{config.homepage.aboutBlurb.text}</p>
                            <Link
                                to={config.homepage.aboutBlurb.linkPath}
                                className="relative inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-md shadow-md
                                       hover:bg-purple-700 hover:shadow-lg transition-all duration-300 ease-in-out group overflow-hidden"
                                style={{ backgroundColor: config.theme.primaryColor }}
                            >
                                {config.homepage.aboutBlurb.linkText}
                                <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                            </Link>
                        </div>
                    </AnimatedSection>
                </section>
            )}

            {/* Homepage Contact Blurb */}
            {config.homepage.showContactBlurb && config.homepage.contactBlurb && (
                <section className="container mx-auto px-6 py-16">
                    <AnimatedSection animation="fade-in-up">
                        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 text-center">
                            <h2 className="text-3xl font-bold text-gray-800 mb-4">{config.homepage.contactBlurb.title}</h2>
                            <p className="text-gray-700 mb-6">{config.homepage.contactBlurb.text}</p>
                            <a
                                href={config.homepage.contactBlurb.buttonLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-md shadow-md
                                       hover:bg-green-700 hover:shadow-lg transition-all duration-300 ease-in-out group overflow-hidden"
                                style={{ backgroundColor: config.theme.accentColor }}
                            >
                                {config.homepage.contactBlurb.buttonText}
                                <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                            </a>
                        </div>
                    </AnimatedSection>
                </section>
            )}
        </div>
    );
};

// --- pages/ProductsPage.js ---
const ProductsPage = () => {
    return (
        <div className="container mx-auto px-6 py-12 font-inter">
            <AnimatedSection animation="fade-in-up">
                <h1 className="text-5xl font-extrabold text-center text-gray-800 mb-12">Our Products</h1>
            </AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {config.products.map((product, index) => (
                    <AnimatedSection key={product.id} animation="fade-in-up" delay={index * 100 + 50}>
                        <ProductCard product={product} />
                    </AnimatedSection>
                ))}
            </div>
        </div>
    );
};

// --- pages/ProductDetailPage.js ---
const ProductDetailPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();

    const mainImageWrapperRef = useRef(null); // Ref for the div that handles mouse/touch events
    const mainImageRef = useRef(null); // Ref for the actual <img> tag for precise dimension calculations
    const thumbnailSliderRef = useRef(null); // Ref for the thumbnail slider container

    // States for product options and quantity
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [selectedPrintSize, setSelectedPrintSize] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState('description');
    const [showAddedToCart, setShowAddedToCart] = useState(false);

    // States for image gallery and zoom
    const [currentImageIndex, setCurrentImageIndex] = useState(0); // This will be the actual index for display
    const [isZoomed, setIsZoomed] = useState(false);
    const [panX, setPanX] = useState(0); // Current pan X offset
    const [panY, setPanY] = useState(0); // Current pan Y offset
    const [isPanning, setIsPanning] = useState(false); // Whether actively dragging (mouse or touch)

    // Refs for touch/mouse interaction tracking
    const dragStartX = useRef(0); // Mouse/Touch X when drag started
    const dragStartY = useRef(0); // Mouse/Touch Y when drag started
    const initialPanX = useRef(0); // panX when drag started
    const initialPanY = useRef(0); // panY when drag started
    const touchMoved = useRef(false); // Flag to track if touch moved significantly (for tap vs. swipe)
    const gestureStarted = useRef(false); // New flag to indicate if a mouse/touch gesture has started
    const isSwipingHorizontal = useRef(false); // New flag for horizontal swipe detection


    // Find the product based on the URL parameter
    const product = useMemo(() => config.products.find(p => p.id === productId), [productId]);

    // --- Consolidated list of all displayable images for the product ---
    // This list will contain all unique image URLs from base, gallery, color options, and print size options.
    const allDisplayImages = useMemo(() => {
        if (!product) return []; // Ensure it returns an array even if product is null
        let images = [];

        // Add base product image
        if (product.imageUrl) {
            images.push(product.imageUrl);
        }

        // Add gallery images
        if (product.galleryImages) {
            images = images.concat(product.galleryImages);
        }

        // Add color-specific images and their print-size variants
        product.colorOptions.forEach(color => {
            if (color.imageUrl && !images.includes(color.imageUrl)) {
                images.push(color.imageUrl);
            }
            if (color.printSizeImages) {
                Object.values(color.printSizeImages).forEach(img => {
                    if (img && !images.includes(img)) {
                        images.push(img);
                    }
                });
            }
        });

        return [...new Set(images)]; // Remove duplicates
    }, [product]); // Dependency on product ensures this recomputes if product data changes

    // If product not found, navigate away
    useEffect(() => {
        if (!product) {
            navigate('/products');
            return;
        }

        // Initialize selections to null for neutral option on product change
        setSelectedColor(null);
        setSelectedPrintSize(null);
        setSelectedSize(product.sizeOptions?.[0] || null);
        setSelectedMaterial(product.materialOptions?.[0] || null);
        setQuantity(1);

        // Determine the initial image to display based on product defaults or first available image.
        // Get the initial image source directly from the product object/config.
        let initialImageSrc = product.imageUrl;
        if (product.galleryImages && product.galleryImages.length > 0) {
            initialImageSrc = product.galleryImages[0];
        }

        // To prevent "Cannot access 'allDisplayImages' before initialization" during initial mount
        // or rapid updates, we'll re-derive a local version of allDisplayImages here for
        // the purpose of setting the initial currentImageIndex.
        let localAllDisplayImages = [];
        if (product.imageUrl) {
            localAllDisplayImages.push(product.imageUrl);
        }
        if (product.galleryImages) {
            localAllDisplayImages = localAllDisplayImages.concat(product.galleryImages);
        }
        product.colorOptions.forEach(color => {
            if (color.imageUrl && !localAllDisplayImages.includes(color.imageUrl)) {
                localAllDisplayImages.push(color.imageUrl);
            }
            if (color.printSizeImages) {
                Object.values(color.printSizeImages).forEach(img => {
                    if (img && !localAllDisplayImages.includes(img)) {
                        localAllDisplayImages.push(img);
                    }
                });
            }
        });
        localAllDisplayImages = [...new Set(localAllDisplayImages)]; // Remove duplicates

        const initialIndex = localAllDisplayImages.indexOf(initialImageSrc);
        setCurrentImageIndex(initialIndex !== -1 ? initialIndex : 0); // Set initial image
        setIsZoomed(false); // Reset zoom
        setPanX(0); // Reset pan
        setPanY(0);

    }, [productId, product, navigate]);


    // Effect to update currentImageIndex based on selected color/print size
    useEffect(() => {
        if (!product) return;

        let newImageToDisplay = null;

        // Check if both color and print size are explicitly selected (not null/neutral)
        const chosenColor = product.colorOptions.find(c => c.name === selectedColor?.name);
        const hasSpecificPrintSize = selectedPrintSize && selectedPrintSize !== 'Select Print Size';

        if (chosenColor && hasSpecificPrintSize && chosenColor.printSizeImages && chosenColor.printSizeImages[selectedPrintSize]) {
            newImageToDisplay = chosenColor.printSizeImages[selectedPrintSize];
        } else if (chosenColor) { // If only color is selected (or print size specific image not found)
            newImageToDisplay = chosenColor.imageUrl;
        } else { // If no specific color or print size selected (or selected as null/neutral), fallback to general images
            newImageToDisplay = allDisplayImages[0] || product.imageUrl;
        }

        const newIndex = allDisplayImages.indexOf(newImageToDisplay);

        // ALWAYS update currentImageIndex to the image corresponding to selected options.
        // This ensures option changes override any manual image navigation (arrows, swipe, thumbnail clicks).
        if (newIndex !== -1) {
            // Temporarily disable transition before setting new state
            if (mainImageRef.current) {
                mainImageRef.current.style.transition = 'none';
            }
            setCurrentImageIndex(newIndex);
            setIsZoomed(false); // Reset zoom on option-driven image change
            setPanX(0);
            setPanY(0);
            // Re-enable transition after state updates, potentially in next frame
            requestAnimationFrame(() => {
                if (mainImageRef.current) {
                    mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out';
                }
            });
        } else if (allDisplayImages.length > 0) {
            // Fallback to the very first image if the calculated newImageToDisplay isn't found
            // (should ideally not happen if allDisplayImages is correctly populated).
            if (mainImageRef.current) {
                mainImageRef.current.style.transition = 'none';
            }
            setCurrentImageIndex(0);
            setIsZoomed(false);
            setPanX(0);
            setPanY(0);
            requestAnimationFrame(() => {
                if (mainImageRef.current) {
                    mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out';
                }
            });
        }
    }, [selectedColor, selectedPrintSize, product, allDisplayImages]);


    // Effect to scroll the selected thumbnail into view whenever currentImageIndex changes
    useEffect(() => {
        if (thumbnailSliderRef.current && currentImageIndex !== null) {
            const selectedThumbnail = thumbnailSliderRef.current.children[currentImageIndex];
            if (selectedThumbnail) {
                // Scroll into view with smooth behavior
                selectedThumbnail.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [currentImageIndex, allDisplayImages]); // Re-run when currentImageIndex or allDisplayImages changes

    const isAddToCartEnabled = selectedColor !== null && selectedSize !== null && selectedMaterial !== null && selectedPrintSize !== null && quantity > 0;

    const handleAddToCart = () => {
        if (isAddToCartEnabled) {
            addToCart(product, selectedColor, selectedSize, selectedMaterial, selectedPrintSize, quantity);
            setShowAddedToCart(true);
            setTimeout(() => setShowAddedToCart(false), 2000);
            window.scrollTo(0, 0); // Ensure scroll to top after adding to cart
        }
    };

    const handleQuantityChange = (delta) => {
        setQuantity(prev => Math.max(1, prev + delta));
    };

    // --- Image Gallery Navigation Handlers (for main viewer) ---
    const showNextImage = useCallback(() => {
        if (mainImageRef.current) {
            mainImageRef.current.style.transition = 'none'; // Disable transition
        }
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % allDisplayImages.length);
        setIsZoomed(false); // Reset zoom on manual image change for better UX
        setPanX(0);
        setPanY(0);
        requestAnimationFrame(() => {
            if (mainImageRef.current) {
                mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out'; // Re-enable
            }
        });
    }, [allDisplayImages.length]);

    const showPrevImage = useCallback(() => {
        if (mainImageRef.current) {
            mainImageRef.current.style.transition = 'none'; // Disable transition
        }
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + allDisplayImages.length) % allDisplayImages.length);
        setIsZoomed(false); // Reset zoom on manual image change for better UX
        setPanX(0);
        setPanY(0);
        requestAnimationFrame(() => {
            if (mainImageRef.current) {
                mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out'; // Re-enable
            }
        });
    }, [allDisplayImages.length]);

    // --- Thumbnail Click Handler ---
    const handleThumbnailClick = useCallback((index) => {
        if (mainImageRef.current) {
            mainImageRef.current.style.transition = 'none'; // Disable transition
        }
        setCurrentImageIndex(index);
        setIsZoomed(false); // Reset zoom on image change
        setPanX(0); // Reset pan on image change
        setPanY(0); // Reset pan on image change
        requestAnimationFrame(() => {
            if (mainImageRef.current) {
                mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out'; // Re-enable
            }
        });
    }, []);

    // --- Zoom and Pan Handlers for the Main Image ---
    const ZOOM_SCALE = 2; // Increased zoom scale for more visible effect

    const toggleZoom = useCallback(() => {
        setIsZoomed(prev => {
            if (mainImageRef.current) { // Ensure ref is not null
                mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out'; // Ensure transition
            }
            if (prev) { // If was zoomed, reset pan on unzoom
                setPanX(0);
                setPanY(0);
            }
            return !prev;
        });
    }, []);


    // Unified gesture start handler for both mouse and touch
    const handleGestureStart = useCallback((e) => {
        if (!mainImageRef.current || !mainImageWrapperRef.current) return;

        // Prevent default for touch starts to avoid immediate scrolling interference,
        // especially when zoomed, or when we intend to handle a swipe.
        if (e.type.startsWith('touch')) {
            e.preventDefault();
        }

        gestureStarted.current = true;
        isSwipingHorizontal.current = false;
        touchMoved.current = false; // Reset for new gesture

        const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
        const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;

        dragStartX.current = clientX;
        dragStartY.current = clientY;
        initialPanX.current = panX; // This *reads* the state panX
        initialPanY.current = panY; // This *reads* the state panY

        if (mainImageRef.current) {
            mainImageRef.current.style.transition = 'none'; // Disable transition during drag/pan
        }

        if (isZoomed) {
            setIsPanning(true);
        }
    }, [isZoomed, panX, panY]); // Added panX, panY to dependency array


    // Unified gesture move handler
    const handleGestureMove = useCallback((e) => {
        if (!gestureStarted.current || !mainImageRef.current || !mainImageWrapperRef.current) return;

        const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
        const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;

        const dx = clientX - dragStartX.current;
        const dy = clientY - dragStartY.current;

        const moveThreshold = 10; // Minimum distance to consider it a "move" or "swipe"

        // Detect significant movement to differentiate tap from drag/swipe
        if (Math.abs(dx) > moveThreshold || Math.abs(dy) > moveThreshold) {
            touchMoved.current = true;
        }

        if (isZoomed && isPanning) {
            e.preventDefault(); // CRITICAL: Prevent page scroll when actively panning a zoomed image.

            const imgElement = mainImageRef.current;
            const container = mainImageWrapperRef.current;

            const baseRenderedWidth = imgElement.offsetWidth;
            const baseRenderedHeight = imgElement.offsetHeight;

            const zoomedWidth = baseRenderedWidth * ZOOM_SCALE;
            const zoomedHeight = baseRenderedHeight * ZOOM_SCALE;

            const containerWidth = container.offsetWidth;
            const containerHeight = container.offsetHeight;

            const maxPanX = Math.max(0, (zoomedWidth - containerWidth) / 2);
            const maxPanY = Math.max(0, (zoomedHeight - containerHeight) / 2);

            let newPanX = initialPanX.current + dx;
            let newPanY = initialPanY.current + dy;

            newPanX = Math.max(-maxPanX, Math.min(maxPanX, newPanX));
            newPanY = Math.max(-maxPanY, Math.min(maxPanY, newPanY));

            setPanX(newPanX);
            setPanY(newPanY);

        } else if (!isZoomed && e.type.startsWith('touch')) {
            // For unzoomed touch gestures, decide whether to prevent default (for horizontal swipe)
            // or allow (for vertical page scroll).
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > moveThreshold) {
                // If horizontal movement is dominant, consider it a horizontal swipe
                isSwipingHorizontal.current = true;
                e.preventDefault(); // Prevent vertical page scroll if horizontal swipe is detected
            } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > moveThreshold) {
                // If vertical movement is dominant, it's a page scroll, do NOT prevent default
                // No action needed, allow browser to scroll the page.
            }
        }
    }, [isZoomed, isPanning, ZOOM_SCALE]);


    // Unified gesture end handler
    const handleGestureEnd = useCallback((e) => {
        if (!gestureStarted.current) return;

        // Re-enable transition on the image element
        if (mainImageRef.current) {
            mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out';
        }

        const wasDragged = touchMoved.current; // True if significant movement occurred during the gesture

        // Reset all gesture-related flags initially
        gestureStarted.current = false;
        setIsPanning(false); // Ensure panning is off
        touchMoved.current = false;
        isSwipingHorizontal.current = false;

        // Determine the action based on the gesture
        if (isZoomed && wasDragged) {
            // If already zoomed and user dragged, it was a pan. Panning stops, no zoom toggle.
            // (setIsPanning is already set to false above)
            return; // No further action for a pan
        } else if (!isZoomed && wasDragged && isSwipingHorizontal.current) {
            // If not zoomed, was dragged, and primarily horizontal (a swipe)
            const swipeDistance = e.type.startsWith('touch') ? e.changedTouches[0].clientX - dragStartX.current : e.clientX - dragStartX.current;
            const minSwipeDistance = 50;

            if (Math.abs(swipeDistance) > minSwipeDistance) {
                if (swipeDistance < 0) { // Swiped left
                    showNextImage();
                } else { // Swiped right
                    showPrevImage();
                }
            }
        } else if (!wasDragged) {
            // If no significant drag/swipe occurred, it's a tap/click. Toggle zoom.
            toggleZoom();
        }
    }, [isZoomed, toggleZoom, showNextImage, showPrevImage]);

    // Handles mouse leaving the wrapper area while dragging
    const handleGestureLeave = useCallback(() => {
        if (gestureStarted.current) {
            if (mainImageRef.current) {
                mainImageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-in-out';
            }
            setIsPanning(false);
            gestureStarted.current = false;
            touchMoved.current = false;
            isSwipingHorizontal.current = false;
        }
    }, []);


    // Handle early return for product not found *after* all hooks are declared
    if (!product) {
        navigate('/products');
        return <div className="text-center py-10 text-gray-600">Product not found.</div>;
    }


    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 font-inter">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                {/* Product Image Gallery & Zoom */}
                <AnimatedSection animation="fade-in" delay={0}>
                    <div className="lg:sticky lg:top-24">
                        <div
                            ref={mainImageWrapperRef} // Assign ref to the wrapper div
                            className="relative w-full h-[400px] sm:h-[500px] overflow-hidden rounded-lg shadow-lg mb-4 sm:mb-6"
                            onMouseDown={handleGestureStart}
                            onMouseMove={handleGestureMove}
                            onMouseUp={handleGestureEnd}
                            onMouseLeave={handleGestureLeave}
                            onTouchStart={handleGestureStart}
                            onTouchMove={handleGestureMove}
                            onTouchEnd={handleGestureEnd}
                            style={{ cursor: isZoomed ? (isPanning ? 'grabbing' : 'grab') : 'zoom-in' }} // Cursor style
                        >
                            <ImageWithSkeleton
                                ref={mainImageRef} // Assign ref to the actual img element
                                src={allDisplayImages[currentImageIndex]} // Use currentImageIndex for main display
                                alt={product.name}
                                className={`w-full h-full transition-transform duration-300 transform`}
                                style={{
                                    objectFit: 'contain', // Always contain within its initial space
                                    transformOrigin: '50% 50%', // Zoom from center
                                    transform: isZoomed ? `translate(${panX}px, ${panY}px) scale(${ZOOM_SCALE})` : 'scale(1)', // Apply translate BEFORE scale
                                }}
                            />
                            {isZoomed && (
                                <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center pointer-events-none">
                                    <span className="text-white text-sm font-bold opacity-75">Drag to pan</span>
                                </div>
                            )}
                            {/* Navigation Arrows */}
                            {allDisplayImages.length > 1 && ( // Arrows are always visible regardless of zoom state
                                <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); showPrevImage(); }}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200 z-10"
                                        aria-label="Previous image"
                                    >
                                        <Icon name="ChevronLeft" className="w-6 h-6" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); showNextImage(); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200 z-10"
                                        aria-label="Next image"
                                    >
                                        <Icon name="ChevronRight" className="w-6 h-6" />
                                    </button>
                                </>
                            )}
                        </div>
                        {/* Thumbnail Slider */}
                        <div className="relative flex items-center justify-center mt-4"> {/* Added justify-center */}
                            {allDisplayImages.length > 3 && ( // Only show arrows if more than 3 thumbnails fit
                                <button
                                    onClick={() => {
                                        if (thumbnailSliderRef.current) {
                                            // Scroll by the width of one thumbnail plus its gap (80px + 12px = 92px)
                                            thumbnailSliderRef.current.scrollBy({ left: -92, behavior: 'smooth' });
                                        }
                                    }}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 bg-gray-200 rounded-full shadow-md hover:bg-gray-300 transition-colors"
                                    aria-label="Scroll thumbnails left"
                                >
                                    <Icon name="ChevronLeft" className="w-5 h-5 text-gray-700" />
                                </button>
                            )}
                            {/* The max-w-[270px] allows showing exactly 3 images (3 * 80px width + 2 * 12px gap = 240 + 24 = 264px) with some padding. */}
                            <div ref={thumbnailSliderRef} className="flex-grow overflow-x-auto whitespace-nowrap scrollbar-hide py-2 px-2 max-w-[270px]">
                                <div className="inline-flex gap-3"> {/* Use inline-flex for scrollable content */}
                                    {allDisplayImages.map((imgUrl, index) => (
                                        <button
                                            key={index}
                                            className={`flex-shrink-0 w-20 h-20 rounded-md border-2 ${currentImageIndex === index ? 'border-purple-600 ring-4 ring-purple-400' : 'border-gray-300'}
                                                       hover:scale-105 transition-transform duration-200 overflow-hidden`}
                                            style={{ backgroundColor: 'white', borderColor: currentImageIndex === index ? config.theme.primaryColor : '' }}
                                            onClick={() => handleThumbnailClick(index)}
                                        >
                                            <ImageWithSkeleton
                                                src={imgUrl}
                                                alt={`${product.name} thumbnail ${index + 1}`}
                                                className="w-full h-full object-cover"
                                                objectFit="cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {allDisplayImages.length > 3 && ( // Only show arrows if more than 3 thumbnails fit
                                <button
                                    onClick={() => {
                                        if (thumbnailSliderRef.current) {
                                            // Scroll by the width of one thumbnail plus its gap (80px + 12px = 92px)
                                            thumbnailSliderRef.current.scrollBy({ left: 92, behavior: 'smooth' });
                                        }
                                    }}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1.5 bg-gray-200 rounded-full shadow-md hover:bg-gray-300 transition-colors"
                                    aria-label="Scroll thumbnails right"
                                >
                                    <Icon name="ChevronRight" className="w-5 h-5 text-gray-700" />
                                </button>
                            )}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Product Details & Customization */}
                <AnimatedSection animation="fade-in-up" delay={100}>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2 sm:mb-3">{product.name}</h1>
                        <p className="text-xl sm:text-2xl font-semibold text-purple-600 mb-4 sm:mb-6" style={{ color: config.theme.primaryColor }}>{APP_CURRENCY_SYMBOL}{product.price.toFixed(2)}</p> {/* Used APP_CURRENCY_SYMBOL */}
                        <p className="text-gray-700 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">{product.longDescription}</p>

                        {/* Customization Options */}
                        <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                            {/* Color Selector */}
                            <div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Color: <span className="font-normal">{selectedColor ? selectedColor.name : 'Select Color'}</span></h3>
                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    {/* Neutral 'Select Color' option */}
                                    <button
                                        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md border text-sm sm:text-base ${selectedColor === null ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-gray-100 text-gray-700 border-gray-300'}
                                                   hover:bg-purple-100 hover:text-purple-800 transition-all duration-200`}
                                        style={{ backgroundColor: selectedColor === null ? config.theme.primaryColor : '', borderColor: selectedColor === null ? config.theme.primaryColor : '' }}
                                        onClick={() => setSelectedColor(null)}
                                    >
                                        Select Color
                                    </button>
                                    {product.colorOptions.map((color) => (
                                        <button
                                            key={color.name}
                                            className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 ${selectedColor?.name === color.name ? 'border-purple-600 ring-4 ring-purple-400' : 'border-gray-300'}
                                                       hover:scale-110 transition-transform duration-200 flex items-center justify-center`}
                                            style={{ backgroundColor: color.hex, borderColor: selectedColor?.name === color.name ? config.theme.primaryColor : '' }}
                                            onClick={() => setSelectedColor(color)}
                                            title={color.name}
                                        >
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Size Selector */}
                            <div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Size: <span className="font-normal">{selectedSize}</span></h3>
                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    {product.sizeOptions.map((size) => (
                                        <button
                                            key={size}
                                            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md border text-sm sm:text-base ${selectedSize === size ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-gray-100 text-gray-700 border-gray-300'}
                                                       hover:bg-purple-100 hover:text-purple-800 transition-all duration-200`}
                                            style={{ backgroundColor: selectedSize === size ? config.theme.primaryColor : '', borderColor: selectedSize === size ? config.theme.primaryColor : '' }}
                                            onClick={() => setSelectedSize(size)}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Material Selector */}
                            <div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Material: <span className="font-normal">{selectedMaterial}</span></h3>
                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    {product.materialOptions.map((material) => (
                                        <button
                                            key={material}
                                            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md border text-sm sm:text-base ${selectedMaterial === material ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-gray-100 text-gray-700 border-gray-300'}
                                                       hover:bg-purple-100 hover:text-purple-800 transition-all duration-200`}
                                            style={{ backgroundColor: selectedMaterial === material ? config.theme.primaryColor : '', borderColor: selectedMaterial === material ? config.theme.primaryColor : '' }}
                                            onClick={() => setSelectedMaterial(material)}
                                        >
                                            {material}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* New: Print Size Selector */}
                            {product.printSizeOptions && product.printSizeOptions.length > 0 && (
                                <div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Print Size: <span className="font-normal">{selectedPrintSize ? selectedPrintSize : 'Select Print Size'}</span></h3>
                                    <div className="flex flex-wrap gap-2 sm:gap-3">
                                        {/* Neutral 'Select Print Size' option */}
                                        <button
                                            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md border text-sm sm:text-base ${selectedPrintSize === null ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-gray-100 text-gray-700 border-gray-300'}
                                                       hover:bg-purple-100 hover:text-purple-800 transition-all duration-200`}
                                            style={{ backgroundColor: selectedPrintSize === null ? config.theme.primaryColor : '', borderColor: selectedPrintSize === null ? config.theme.primaryColor : '' }}
                                            onClick={() => setSelectedPrintSize(null)}
                                        >
                                            Select Print Size
                                        </button>
                                        {product.printSizeOptions.map((printSize) => (
                                            <button
                                                key={printSize}
                                                className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md border text-sm sm:text-base ${selectedPrintSize === printSize ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-gray-100 text-gray-700 border-gray-300'}
                                                           hover:bg-purple-100 hover:text-purple-800 transition-all duration-200`}
                                                style={{ backgroundColor: selectedPrintSize === printSize ? config.theme.primaryColor : '', borderColor: selectedPrintSize === printSize ? config.theme.primaryColor : '' }}
                                                onClick={() => setSelectedPrintSize(printSize)}
                                            >
                                                {printSize}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}


                            {/* Quantity Selector */}
                            <div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Quantity:</h3>
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <button
                                        onClick={() => handleQuantityChange(-1)}
                                        className="p-1.5 sm:p-2 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex-shrink-0"
                                        disabled={quantity <= 1} // Corrected: use `quantity` from state
                                    >
                                        <Icon name="Minus" className="w-4 h-4 text-gray-700" />
                                    </button>
                                    <span className="mx-2 text-lg sm:text-xl font-semibold text-gray-800 w-8 sm:w-10 text-center">{quantity}</span>
                                    <button
                                        onClick={() => handleQuantityChange(1)}
                                        className="p-1.5 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex-shrink-0"
                                    >
                                        <Icon name="Plus" className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Add to Cart Button */}
                        <button
                            onClick={handleAddToCart}
                            disabled={!isAddToCartEnabled}
                            className={`relative w-full py-3 sm:py-4 text-lg sm:text-xl font-bold rounded-md shadow-lg transition-all duration-300 ease-in-out group overflow-hidden
                                       ${isAddToCartEnabled ? 'text-white hover:shadow-xl hover:scale-105' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                            style={{ backgroundColor: isAddToCartEnabled ? config.theme.accentColor : '' }}
                        >
                            {showAddedToCart ? (
                                <span className="flex items-center justify-center space-x-2">
                                    <span>Added to Cart!</span>
                                </span>
                            ) : (
                                isAddToCartEnabled ? 'Add to Cart' : 'Select Options'
                            )}
                            <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                        </button>
                    </div>
                </AnimatedSection>
            </div>

            {/* Information Sections */}
            <AnimatedSection animation="fade-in-up" delay={200}>
                <div className="mt-12 sm:mt-16 bg-white rounded-lg shadow-md p-6 sm:p-8">
                    {/* Desktop Tabs */}
                    <div className="hidden sm:flex border-b border-gray-200 mb-4 sm:mb-6">
                        {['description', 'specification', 'reviews', 'faq'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 sm:px-6 sm:py-3 text-base sm:text-lg font-medium capitalize border-b-2 transition-all duration-300
                                           ${activeTab === tab ? 'text-purple-700' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                                style={{ borderColor: activeTab === tab ? config.theme.primaryColor : '', color: activeTab === tab ? config.theme.primaryColor : '' }}
                            >
                                {tab.replace('-', ' ')}
                            </button>
                        ))}
                    </div>

                    {/* Mobile Accordion */}
                    <div className="sm:hidden space-y-4">
                        <details className="group rounded-lg bg-gray-50 p-3 shadow-sm cursor-pointer border border-gray-200">
                            <summary className="flex justify-between items-center font-medium text-gray-800 text-base">
                                Product Description
                                <span className="transition-transform duration-300 group-open:rotate-180">
                                    <Icon name="ChevronDown" className="w-5 h-5" />
                                </span>
                            </summary>
                            <div className="mt-2 text-gray-600 text-sm">
                                {product.longDescription}
                            </div>
                        </details>
                        <details className="group rounded-lg bg-gray-50 p-3 shadow-sm cursor-pointer border border-gray-200">
                            <summary className="flex justify-between items-center font-medium text-gray-800 text-base">
                                Specifications
                                <span className="transition-transform duration-300 group-open:rotate-180">
                                    <Icon name="ChevronDown" className="w-5 h-5" />
                                </span>
                            </summary>
                            <ul className="mt-2 list-disc list-inside space-y-1 text-gray-600 text-sm">
                                {product.specifications.map((spec, index) => (
                                    <li key={index}><strong>{spec.title}:</strong> {spec.detail}</li>
                                ))}
                            </ul>
                        </details>
                        <details className="group rounded-lg bg-gray-50 p-3 shadow-sm cursor-pointer border border-gray-200">
                            <summary className="flex justify-between items-center font-medium text-gray-800 text-base">
                                Customer Reviews
                                <span className="transition-transform duration-300 group-open:rotate-180">
                                    <Icon name="ChevronDown" className="w-5 h-5" />
                                </span>
                            </summary>
                            <div className="mt-2 text-gray-600 text-sm">
                                {config.reviews.filter(r => r.id === 'rev-1' || r.id === 'rev-2').map(review => ( // Showing a couple of general reviews for demo
                                    <div key={review.id} className="mb-3 pb-3 border-b border-gray-100 last:border-b-0">
                                        <h4 className="font-semibold text-gray-800 text-base">{review.customerName}</h4>
                                        <div className="flex text-yellow-500 mb-1">
                                            {Array(review.rating).fill(0).map((_, i) => <Icon key={i} name="Star" className="w-4 h-4 fill-current" />)}
                                            {Array(5 - review.rating).fill(0).map((_, i) => <Icon key={i} name="Star" className="w-4 h-4" />)}
                                        </div>
                                        <p className="text-gray-600 text-sm italic">"{review.text}"</p>
                                    </div>
                                ))}
                                {/* Corrected to use Link from react-router-dom */}
                                <Link to="/reviews" className="text-purple-600 hover:underline mt-3 inline-block" style={{ color: config.theme.primaryColor }}>View All Reviews</Link>
                            </div>
                        </details>
                        <details className="group rounded-lg bg-gray-50 p-3 shadow-sm cursor-pointer border border-gray-200">
                            <summary className="flex justify-between items-center font-medium text-gray-800 text-base">
                                Frequently Asked Questions
                                <span className="transition-transform duration-300 group-open:rotate-180">
                                    <Icon name="ChevronDown" className="w-5 h-5" />
                                </span>
                            </summary>
                            <div className="mt-2 text-gray-600 text-sm">
                                <div className="space-y-3">
                                    {product.faqs.map((faq, index) => (
                                        <div key={index}>
                                            <p className="font-semibold text-gray-800">{faq.question}</p>
                                            <p className="text-gray-600">{faq.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </details>
                    </div>

                    {/* Content based on active tab (Desktop only) */}
                    <div className="hidden sm:block prose max-w-none text-gray-700 text-sm sm:text-base">
                        {activeTab === 'description' && (
                            <div>
                                <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Product Description</h3>
                                <p>{product.longDescription}</p>
                            </div>
                        )}
                        {activeTab === 'specification' && (
                            <div>
                                <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Specifications</h3>
                                <ul className="list-disc list-inside space-y-2">
                                    {product.specifications.map((spec, index) => (
                                        <li key={index}><strong>{spec.title}:</strong> {spec.detail}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {activeTab === 'reviews' && (
                            <div>
                                <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Customer Reviews</h3>
                                {config.reviews.filter(r => r.id === 'rev-1' || r.id === 'rev-2').map(review => ( // Showing a couple of general reviews for demo
                                    <div key={review.id} className="mb-3 pb-3 border-b border-gray-100 last:border-b-0">
                                        <h4 className="font-semibold text-gray-800 text-base sm:text-lg">{review.customerName}</h4>
                                        <div className="flex text-yellow-500 mb-1">
                                            {Array(review.rating).fill(0).map((_, i) => <Icon key={i} name="Star" className="w-4 h-4 fill-current" />)}
                                            {Array(5 - review.rating).fill(0).map((_, i) => <Icon key={i} name="Star" className="w-4 h-4" />)}
                                        </div>
                                        <p className="text-gray-600 text-sm italic">"{review.text}"</p>
                                    </div>
                                ))}
                                {/* Corrected to use Link from react-router-dom */}
                                <Link to="/reviews" className="text-purple-600 hover:underline mt-3 inline-block" style={{ color: config.theme.primaryColor }}>View All Reviews</Link>
                            </div>
                        )}
                        {activeTab === 'faq' && (
                            <div>
                                <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Frequently Asked Questions</h3>
                                <div className="space-y-3 sm:space-y-4">
                                    {product.faqs.map((faq, index) => (
                                        <details key={index} className="group rounded-lg bg-gray-50 p-3 sm:p-4 shadow-sm cursor-pointer">
                                            <summary className="flex justify-between items-center font-medium text-gray-800 text-base sm:text-lg">
                                                {faq.question}
                                                <span className="transition-transform duration-300 group-open:rotate-180">
                                                    <Icon name="ChevronDown" className="w-5 h-5" />
                                                </span>
                                            </summary>
                                            <p className="mt-2 text-gray-600 text-sm sm:text-base">{faq.answer}</p>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AnimatedSection>

            {/* Similar Products Section */}
            <section className="mt-12 sm:mt-16 py-6 sm:py-8">
                <AnimatedSection animation="fade-in-up">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-6 sm:mb-8 pb-4 border-b-2 border-purple-200" style={{ borderColor: config.theme.primaryColor + '40' }}>
                        Similar Products
                    </h2>
                </AnimatedSection>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {config.products
                        .filter(p => p.id !== productId) // Exclude current product
                        .slice(0, 3) // Show up to 3 similar products
                        .map((product, index) => (
                            <AnimatedSection key={product.id} animation="fade-in-up" delay={index * 200 + 100}>
                                <ProductCard product={product} />
                            </AnimatedSection>
                        ))}
                </div>
            </section>
        </div>
    );
};

// --- pages/CartPage.js ---
const CartPage = () => {
    const { cartItems, removeFromCart, updateQuantity, clearCart, shippingInfo, updateShippingInfo, clearShippingInfo, getTotalPrice } = useCart();
    // const navigate = useNavigate(); // Removed this line as it was causing ESLint warnings

    const [shippingForm, setShippingForm] = useState(shippingInfo);
    const [isClearCartModalOpen, setIsClearCartModalOpen] = useState(false);
    const [isClearShippingModalOpen, setIsClearShippingModalOpen] = useState(false);

    useEffect(() => {
        setShippingForm(shippingInfo); // Sync form with context on load
    }, [shippingInfo]);

    const handleShippingChange = (e) => {
        const { name, value } = e.target;
        setShippingForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveShippingInfo = () => {
        updateShippingInfo(shippingForm);
        // Optionally, show a confirmation message
    };

    const validateShippingInfo = () => {
        const { fullName, streetAddress, city, postalCode, phoneNumber } = shippingForm;
        // Check if all required fields are non-empty and phone number has at least 10 digits
        return cartItems.length > 0 &&
               fullName && fullName.trim() !== '' &&
               streetAddress && streetAddress.trim() !== '' &&
               city && city.trim() !== '' &&
               postalCode && postalCode.trim() !== '' &&
               phoneNumber && phoneNumber.length >= 10;
    };

    const handleProceedToCheckout = () => {
        if (!validateShippingInfo()) {
            alert("Please fill in all required shipping details and ensure you have items in your cart.");
            return;
        }

        handleSaveShippingInfo(); // Ensure latest info is saved

        // Construct the WhatsApp message using literal newlines (\n) and markdown for bold/italic
        let whatsappMessage = `${config.whatsappOrderHeader}\n\n`; // Use configurable header
        whatsappMessage += `*Order Summary:*\n`;

        cartItems.forEach((item, index) => {
            whatsappMessage += `_Item ${index + 1}:_\n`;
            whatsappMessage += `*Product:* ${item.name}\n`;
            whatsappMessage += `*Color:* ${item.color}\n`;
            whatsappMessage += `*Size:* ${item.size}\n`;
            whatsappMessage += `*Material:* ${item.material}\n`;
            if (item.printSize) { // New: Include print size if available
                whatsappMessage += `*Print Size:* ${item.printSize}\n`;
            }
            whatsappMessage += `*Quantity:* ${item.quantity}\n`;
            whatsappMessage += `*Price:* ${APP_CURRENCY_SYMBOL}${item.price.toFixed(2)} each\n\n`; // Use configurable currency symbol
        });

        whatsappMessage += `*Total Order: ${APP_CURRENCY_SYMBOL}${getTotalPrice().toFixed(2)}*\n\n`; // Use configurable currency symbol
        whatsappMessage += `*Shipping Information:*\n`;
        whatsappMessage += `*Name:* ${shippingForm.fullName}\n`;
        // Combined address into one line
        whatsappMessage += `*Address:* ${shippingForm.streetAddress}, ${shippingForm.city}, ${shippingForm.postalCode}\n`;
        whatsappMessage += `*Phone:* ${shippingForm.phoneNumber}\n`;
        if (shippingForm.emailAddress) {
            whatsappMessage += `*Email:* ${shippingForm.emailAddress}\n\n`;
        } else {
            whatsappMessage += `\n`; // Add extra newline if no email
        }
        whatsappMessage += `${config.whatsappOrderFooter}`; // Use configurable footer

        // Encode the entire message for the URL
        const encodedMessage = encodeURIComponent(whatsappMessage);
        const whatsappUrl = `https://wa.me/${APP_WHATSAPP_NUMBER}?text=${encodedMessage}`; // Use constant

        window.open(whatsappUrl, '_blank'); // Open in new tab
        // Optionally, clear cart after successful WhatsApp redirection
        // clearCart();
        // clearShippingInfo();
    };

    const confirmClearCart = () => {
        clearCart();
        setIsClearCartModalOpen(false);
    };

    const confirmClearShippingInfo = () => {
        clearShippingInfo();
        setShippingForm({}); // Also clear local form state
        setIsClearShippingModalOpen(false);
    };

    // Determine if shipping info is empty to disable clear button
    const isShippingInfoEmpty = Object.keys(shippingInfo).length === 0 || Object.values(shippingInfo).every(value => !value || value.trim() === '');


    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 font-inter">
            <AnimatedSection animation="fade-in-up">
                <h1 className="text-4xl sm:text-5xl font-extrabold text-center text-gray-800 mb-8 sm:mb-12 pb-4 border-b-2 border-purple-200" style={{ borderColor: config.theme.primaryColor + '40' }}>
                    Your Cart
                </h1>
            </AnimatedSection>

            {cartItems.length === 0 ? (
                <AnimatedSection animation="fade-in" delay={200}>
                    <div className="text-center py-10 text-gray-600 text-lg sm:text-xl">
                        Your cart is empty. <Link to="/products" className="text-purple-600 hover:underline" style={{ color: config.theme.primaryColor }}>Start shopping!</Link>
                    </div>
                </AnimatedSection>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Cart Items List */}
                    <AnimatedSection animation="fade-in-up" delay={100} className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 sm:p-8 border border-gray-200">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 pb-3 border-b border-gray-200">Items in Cart</h2>
                        <div className="space-y-6">
                            {cartItems.map((item) => (
                                <div key={item.id} className="flex flex-col sm:flex-row items-center sm:items-start border-b border-gray-200 pb-4 last:border-b-0 last:pb-0 mb-6 sm:mb-4">
                                    <ImageWithSkeleton
                                        src={item.imageUrl}
                                        alt={item.name}
                                        className="w-24 h-24 object-cover rounded-md mb-4 sm:mb-0 sm:mr-4 shadow-sm flex-shrink-0"
                                    />
                                    <div className="flex-grow w-full text-center sm:text-left">
                                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">{item.name}</h3>
                                        <p className="text-sm text-gray-600">
                                            Color: {item.color}, Size: {item.size}, Material: {item.material}
                                            {item.printSize && `, Print Size: ${item.printSize}`} {/* New: Display print size */}
                                        </p>
                                        <p className="text-base sm:text-md font-medium text-purple-600 mt-1" style={{ color: config.theme.primaryColor }}>{APP_CURRENCY_SYMBOL}{item.price.toFixed(2)} each</p> {/* Used APP_CURRENCY_SYMBOL */}
                                        <div className="flex items-center justify-center sm:justify-start mt-2 w-full">
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                className="p-1.5 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex-shrink-0"
                                                disabled={item.quantity <= 1}
                                            >
                                                <Icon name="Minus" className="w-4 h-4 text-gray-700" />
                                            </button>
                                            <span className="mx-2 text-base sm:text-md font-semibold min-w-[30px] text-center">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                className="p-1.5 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex-shrink-0"
                                            >
                                                <Icon name="Plus" className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                                            </button>
                                            <span className="ml-auto text-lg sm:text-xl font-bold text-gray-800 hidden sm:block flex-shrink-0">
                                                {APP_CURRENCY_SYMBOL}{(item.price * item.quantity).toFixed(2)}
                                            </span> {/* Used APP_CURRENCY_SYMBOL */}
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="ml-2 sm:ml-4 px-2 py-1 text-red-600 hover:text-red-800 transition-colors duration-200 text-sm sm:text-base flex-shrink-0 rounded-md border border-red-300 hover:border-red-500"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <span className="ml-auto text-lg sm:text-xl font-bold text-gray-800 block sm:hidden mt-2">
                                            {APP_CURRENCY_SYMBOL}{(item.price * item.quantity).toFixed(2)}
                                        </span> {/* Used APP_CURRENCY_SYMBOL */}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between items-center mt-6 sm:mt-8 pt-4 border-t border-gray-200">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-800">Total:</h3>
                            <span className="text-2xl sm:text-3xl font-extrabold text-purple-700" style={{ color: config.theme.primaryColor }}>{APP_CURRENCY_SYMBOL}{getTotalPrice().toFixed(2)}</span> {/* Used APP_CURRENCY_SYMBOL */}
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => setIsClearCartModalOpen(true)}
                                className="relative px-5 py-2.5 sm:px-6 sm:py-3 bg-red-600 text-white font-medium rounded-md shadow-md text-sm sm:text-base
                                           hover:bg-red-700 hover:shadow-lg transition-all duration-300 ease-in-out group overflow-hidden"
                            >
                                Clear Cart
                                <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                            </button>
                        </div>
                    </AnimatedSection>

                    {/* Shipping Information & Checkout */}
                    <AnimatedSection animation="fade-in-up" delay={200} className="bg-white rounded-lg shadow-md p-6 sm:p-8 border border-gray-200">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 pb-3 border-b border-gray-200">Shipping Information</h2>
                        <form className="space-y-4">
                            <div>
                                <label htmlFor="fullName" className="block text-gray-700 text-sm sm:text-base font-bold mb-2">Full Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={shippingForm.fullName || ''}
                                    onChange={handleShippingChange}
                                    className="shadow appearance-none border rounded-md w-full py-2.5 px-3 sm:py-3 sm:px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm sm:text-base"
                                    placeholder="John Doe"
                                    required
                                    style={{ '--tw-ring-color': config.theme.primaryColor }}
                                />
                            </div>
                            <div>
                                <label htmlFor="streetAddress" className="block text-gray-700 text-sm sm:text-base font-bold mb-2">Street Address <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    id="streetAddress"
                                    name="streetAddress"
                                    value={shippingForm.streetAddress || ''}
                                    onChange={handleShippingChange}
                                    className="shadow appearance-none border rounded-md w-full py-2.5 px-3 sm:py-3 sm:px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm sm:text-base"
                                    placeholder="123 Main St"
                                    required
                                    style={{ '--tw-ring-color': config.theme.primaryColor }}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="city" className="block text-gray-700 text-sm sm:text-base font-bold mb-2">City <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        value={shippingForm.city || ''}
                                        onChange={handleShippingChange}
                                        className="shadow appearance-none border rounded-md w-full py-2.5 px-3 sm:py-3 sm:px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm sm:text-base"
                                        placeholder="Lahore"
                                        required
                                        style={{ '--tw-ring-color': config.theme.primaryColor }}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="postalCode" className="block text-gray-700 text-sm sm:text-base font-bold mb-2">Postal Code <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        id="postalCode"
                                        name="postalCode"
                                        value={shippingForm.postalCode || ''}
                                        onChange={handleShippingChange}
                                        className="shadow appearance-none border rounded-md w-full py-2.5 px-3 sm:py-3 sm:px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm sm:text-base"
                                    placeholder="54000"
                                    required
                                    style={{ '--tw-ring-color': config.theme.primaryColor }}
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="phoneNumber" className="block text-gray-700 text-sm sm:text-base font-bold mb-2">Phone Number <span className="text-red-500">*</span></label>
                            <input
                                type="tel"
                                id="phoneNumber"
                                name="phoneNumber"
                                value={shippingForm.phoneNumber || ''}
                                onChange={handleShippingChange}
                                className="shadow appearance-none border rounded-md w-full py-2.5 px-3 sm:py-3 sm:px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm sm:text-base"
                                placeholder="03XX-XXXXXXX"
                                required
                                style={{ '--tw-ring-color': config.theme.primaryColor }}
                            />
                        </div>
                        <div>
                            <label htmlFor="emailAddress" className="block text-gray-700 text-sm sm:text-base font-bold mb-2">Email Address (Optional)</label>
                            <input
                                type="email"
                                id="emailAddress"
                                name="emailAddress"
                                value={shippingForm.emailAddress || ''}
                                onChange={handleShippingChange}
                                className="shadow appearance-none border rounded-md w-full py-2.5 px-3 sm:py-3 sm:px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm sm:text-base"
                                placeholder="john.doe@example.com"
                                style={{ '--tw-ring-color': config.theme.primaryColor }}
                            />
                        </div>
                    </form>
                    <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center sm:items-end mt-6 space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                            onClick={() => setIsClearShippingModalOpen(true)}
                            disabled={isShippingInfoEmpty}
                            className={`relative px-5 py-2.5 sm:px-6 sm:py-3 font-medium rounded-md shadow-md text-sm sm:text-base
                                           transition-all duration-300 ease-in-out group overflow-hidden w-full sm:w-auto
                                           ${isShippingInfoEmpty ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-white hover:shadow-lg'}`}
                            style={{ backgroundColor: isShippingInfoEmpty ? '' : config.theme.primaryColor }}
                        >
                            Clear Shipping Info
                            <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                        </button>
                        <button
                            onClick={handleProceedToCheckout}
                            disabled={!validateShippingInfo()}
                            title={!validateShippingInfo() ? "Please enter all required shipping info above" : ""}
                            className={`relative px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-xl font-bold rounded-md shadow-lg transition-all duration-300 ease-in-out group overflow-hidden w-full sm:w-auto
                                           ${validateShippingInfo() ? 'text-white hover:shadow-xl hover:scale-105' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                            style={{ backgroundColor: validateShippingInfo() ? config.theme.accentColor : '' }}
                        >
                            Checkout through Whatsapp
                            <span className="absolute inset-0 border-2 border-transparent rounded-md transition-all duration-300 ease-in-out group-hover:border-white group-hover:border-opacity-50"></span>
                        </button>
                    </div>
                </AnimatedSection>
            </div>
            )}

            {/* Modals for confirmation */}
            <Modal
                isOpen={isClearCartModalOpen}
                onClose={() => setIsClearCartModalOpen(false)}
                onConfirm={confirmClearCart}
                title="Clear Cart"
                message="Are you sure you want to clear all items from your cart? This action cannot be undone."
            />
            <Modal
                isOpen={isClearShippingModalOpen}
                onClose={() => setIsClearShippingModalOpen(false)}
                onConfirm={confirmClearShippingInfo}
                title="Clear Shipping Information"
                message="Are you sure you want to clear your shipping details? You will have to re-enter them."
            />
        </div>
    );
};

// --- pages/ReviewsPage.js ---
const ReviewsPage = () => {
    return (
        <div className="container mx-auto px-6 py-12 font-inter">
            <AnimatedSection animation="fade-in-up">
                <h1 className="text-5xl font-extrabold text-center text-gray-800 mb-12">Customer Reviews</h1>
            </AnimatedSection>
            <AnimatedSection animation="fade-in-up" delay={100}>
                <ReviewsSlider reviews={config.reviews} />
            </AnimatedSection>
            <AnimatedSection animation="fade-in-up" delay={200}>
                <p className="text-center text-gray-600 mt-8 text-lg">
                    We value your feedback! Thank you to all our amazing customers.
                </p>
            </AnimatedSection>
        </div>
    );
};

// --- pages/AboutPage.js ---
const AboutPage = () => {
    // Removed renderContent function as it is no longer used.

    return (
        <div className="container mx-auto px-6 py-12 font-inter">
            <AnimatedSection animation="fade-in-up">
                <h1 className="text-5xl font-extrabold text-center text-gray-800 mb-12">About Us</h1>
            </AnimatedSection>

            <AnimatedSection animation="fade-in-up" delay={100}>
                <section className="bg-white rounded-lg shadow-md p-8 mb-10 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                    style={{ borderColor: config.theme.primaryColor + '20' }}>
                    <h2 className="text-3xl font-bold text-purple-700 mb-6 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                        style={{ color: config.theme.primaryColor }}>
                        <Icon name="Info" className="w-8 h-8 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> Our Story
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        Welcome to {config.shopName}, where creativity meets comfort! Founded in 2025 by Noman, our journey began with a simple passion: to empower individuals to express themselves through unique, high-quality custom apparel. We believe that your clothing should be as unique as you are.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                        We specialize in Direct-to-Film (DTF) printing, a cutting-edge technology that delivers incredibly vibrant colors, sharp details, and exceptional durability. Every T-shirt we print is a canvas for your imagination, meticulously crafted to ensure it stands out and lasts.
                    </p>
                </section>
            </AnimatedSection>

            <AnimatedSection animation="fade-in-up" delay={200}>
                <section className="bg-white rounded-lg shadow-md p-8 mb-10 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                    style={{ borderColor: config.theme.primaryColor + '20' }}>
                    <h2 className="text-3xl font-bold text-purple-700 mb-6 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                        style={{ color: config.theme.primaryColor }}>
                        <Icon name="Package" className="w-8 h-8 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> How It Works
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                        <div className="p-4 rounded-md bg-purple-50 bg-opacity-70 shadow-sm" style={{ backgroundColor: config.theme.primaryColor + '10' }}>
                            <Icon name="Shirt" className="w-12 h-12 text-purple-600 mx-auto mb-4" style={{ color: config.theme.primaryColor }} />
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">1. Choose Your Product</h3>
                            <p className="text-gray-600 text-sm">Browse our selection of premium DTF T-shirts and apparel.</p>
                        </div>
                        <div className="p-4 rounded-md bg-purple-50 bg-opacity-70 shadow-sm" style={{ backgroundColor: config.theme.primaryColor + '10' }}>
                            <Icon name="User" className="w-12 h-12 text-purple-600 mx-auto mb-4" style={{ color: config.theme.primaryColor }} />
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">2. Customize Your Design</h3>
                            <p className="text-gray-600 text-sm">Select colors, sizes, materials, and quantities. For custom designs, connect with us!</p>
                        </div>
                        <div className="p-4 rounded-md bg-purple-50 bg-opacity-70 shadow-sm" style={{ backgroundColor: config.theme.primaryColor + '10' }}>
                            <Icon name="ShoppingCart" className="w-12 h-12 text-purple-600 mx-auto mb-4" style={{ color: config.theme.primaryColor }} />
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">3. Order via WhatsApp</h3>
                            <p className="text-gray-600 text-sm">Add to cart, fill shipping info, and send your order directly to us on WhatsApp for a quick confirmation.</p>
                        </div>
                    </div>
                </section>
            </AnimatedSection>

            <AnimatedSection animation="fade-in-up" delay={300}>
                <section className="bg-white rounded-lg shadow-md p-8 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                    style={{ borderColor: config.theme.primaryColor + '20' }}>
                        <h2 className="text-3xl font-bold text-purple-700 mb-6 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                        style={{ color: config.theme.primaryColor }}>
                        <Icon name="Package" className="w-8 h-8 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> What We Offer
                    </h2>
                    <ul className="list-disc list-inside space-y-3 text-gray-700 leading-relaxed">
                        <li><strong>Superior DTF Printing:</strong> Unmatched vibrancy, detail, and durability for your designs.</li>
                        <li><strong>Premium Quality Apparel:</strong> Comfortable, long-lasting T-shirts and hoodies that feel as good as they look.</li>
                        <li><strong>Personalized Service:</strong> We're here to help you bring your vision to life, from design assistance to order support.</li>
                        <li><strong>Seamless WhatsApp Ordering:</strong> A convenient and direct way to place your custom orders.</li>
                        <li><strong>Fast & Reliable Shipping:</strong> Get your custom apparel delivered right to your doorstep.</li>
                    </ul>
                </section>
            </AnimatedSection>
        </div>
    );
};

// --- pages/PolicyPage.js ---
const PolicyPage = () => {
    // Removed renderContent function as it is no longer used.

    return (
        <div className="container mx-auto px-6 py-12 font-inter">
            <AnimatedSection animation="fade-in-up">
                <h1 className="text-5xl font-extrabold text-center text-gray-800 mb-12">Our Policies</h1>
            </AnimatedSection>

            <div className="bg-white rounded-lg shadow-md p-8 space-y-10">
                {/* Privacy Policy */}
                <AnimatedSection animation="fade-in-up" delay={100}>
                    <section className="bg-gray-50 rounded-lg shadow-sm p-8 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                        style={{ backgroundColor: config.theme.lightBgColor, borderColor: config.theme.primaryColor + '20' }}>
                        <h2 className="text-3xl font-bold text-purple-700 mb-4 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                            style={{ color: config.theme.primaryColor }}>
                            <Icon name="FileText" className="w-7 h-7 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> {config.policy.privacyPolicy.title}
                        </h2>
                        {config.policy.privacyPolicy.content.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-gray-700 leading-relaxed mb-3">
                                {paragraph.split('\n').map((line, lineIndex) => (
                                    <React.Fragment key={lineIndex}>
                                        {line.startsWith('**') ? <strong>{line.substring(2, line.length - 2)}</strong> : line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </p>
                        ))}
                    </section>
                </AnimatedSection>

                {/* Refund Policy */}
                <AnimatedSection animation="fade-in-up" delay={200}>
                    <section className="bg-white rounded-lg shadow-sm p-8 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                        style={{ borderColor: config.theme.primaryColor + '20' }}>
                        <h2 className="text-3xl font-bold text-purple-700 mb-4 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                            style={{ color: config.theme.primaryColor }}>
                            <Icon name="FileText" className="w-7 h-7 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> {config.policy.refundPolicy.title}
                        </h2>
                        {config.policy.refundPolicy.content.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-gray-700 leading-relaxed mb-3">
                                {paragraph.split('\n').map((line, lineIndex) => (
                                    <React.Fragment key={lineIndex}>
                                        {line.startsWith('**') ? <strong>{line.substring(2, line.length - 2)}</strong> : line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </p>
                        ))}
                    </section>
                </AnimatedSection>

                {/* Shipping Policy */}
                <AnimatedSection animation="fade-in-up" delay={300}>
                    <section className="bg-gray-50 rounded-lg shadow-sm p-8 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                        style={{ backgroundColor: config.theme.lightBgColor, borderColor: config.theme.primaryColor + '20' }}>
                        <h2 className="text-3xl font-bold text-purple-700 mb-4 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                            style={{ color: config.theme.primaryColor }}>
                            <Icon name="FileText" className="w-7 h-7 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> {config.policy.shippingPolicy.title}
                        </h2>
                        {config.policy.shippingPolicy.content.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-gray-700 leading-relaxed mb-3">
                                {paragraph.split('\n').map((line, lineIndex) => (
                                    <React.Fragment key={lineIndex}>
                                        {line.startsWith('**') ? <strong>{line.substring(2, line.length - 2)}</strong> : line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </p>
                        ))}
                    </section>
                </AnimatedSection>

                {/* Terms of Service */}
                <AnimatedSection animation="fade-in-up" delay={400}>
                    <section className="bg-white rounded-lg shadow-sm p-8 transition-all duration-300 ease-in-out hover:shadow-lg hover:border-purple-200 border border-transparent group"
                        style={{ borderColor: config.theme.primaryColor + '20' }}>
                        <h2 className="text-3xl font-bold text-purple-700 mb-4 flex items-center group-hover:text-purple-800 transition-colors duration-300"
                            style={{ color: config.theme.primaryColor }}>
                            <Icon name="FileText" className="w-7 h-7 mr-3 text-purple-500 group-hover:text-purple-600 transition-colors duration-300" style={{ color: config.theme.primaryColor }} /> {config.policy.termsOfService.title}
                        </h2>
                        {config.policy.termsOfService.content.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-gray-700 leading-relaxed mb-3">
                                {paragraph.split('\n').map((line, lineIndex) => (
                                    <React.Fragment key={lineIndex}>
                                        {line.startsWith('**') ? <strong>{line.substring(2, line.length - 2)}</strong> : line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </p>
                        ))}
                    </section>
                </AnimatedSection>
            </div>
        </div>
    );
};


// New component to handle scrolling to top on route change
const ScrollToTop = () => {
    const location = useLocation();
    useEffect(() => {
        // This useEffect will now only serve as a general fallback/debug.
        // The primary scroll-to-top is now handled by explicit onClick handlers on Links.
        // console.log("ScrollToTop component triggered for path:", location.pathname);
        const timer = setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }, 0); // Very small delay to ensure render tree is ready
        return () => clearTimeout(timer);
    }, [location.pathname]);
    return null; // This component doesn't render anything
};

// --- WhatsAppButton.js ---
const WhatsAppButton = () => {
    const whatsappUrl = `https://wa.me/${APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(config.whatsappPreMessage)}`; // Use constant

    return (
        <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg
                       flex items-center justify-center space-x-2
                       hover:bg-green-600 hover:shadow-xl hover:scale-110
                       transition-all duration-300 ease-in-out transform
                       border-2 border-green-400 hover:border-green-300
                       group z-50"
            aria-label="Chat with us on WhatsApp"
            style={{ backgroundColor: config.theme.accentColor }}
        >
            <Icon name="Whatsapp" className="w-7 h-7 text-white group-hover:text-green-100 transition-colors duration-300" />
            <span className="hidden md:block text-lg font-semibold group-hover:text-green-100 transition-colors duration-300">
                WhatsApp Us
            </span>
        </a>
    );
};


// --- App.js ---
function App() {
    return (
        <Router>
            {/* ScrollToTop must be rendered inside the Router context */}
            <ScrollToTop />
            <CartProvider>
                <div className="min-h-screen flex flex-col bg-gray-100" style={{ backgroundColor: config.theme.lightBgColor }}>
                    <Header />
                    <main className="flex-grow">
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/products" element={<ProductsPage />} />
                            <Route path="/products/:productId" element={<ProductDetailPage />} />
                            <Route path="/cart" element={<CartPage />} />
                            <Route path="/reviews" element={<ReviewsPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/policy" element={<PolicyPage />} />
                        </Routes>
                    </main>
                    <Footer />
                    <WhatsAppButton />
                </div>
            </CartProvider>
        </Router>
    );
}

export default App;
